import PropTypes from "prop-types";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./RecruiterDashboard.css";
import SalarySlipCalculator from "../Frontend/Calculator/SalarySlip/SalarySlipCalculator";
import EmployeeDocumentManagement from "./Features/DocumentManagement/EmpDocument";
import EmpManage from "./Features/EmployeeManagement/EmployeeManageApi";
import Payroll from "./Features/PayrollManagement/Payroll";

// ─── API base + auth helpers ────────────────────────────────────────
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

async function readJsonResponse(res) {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

function readToken() {
  return (
    localStorage.getItem("th_employer_token") ||
    localStorage.getItem("th_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

function readStoredUser() {
  try {
    const raw =
      localStorage.getItem("th_employer") ||
      localStorage.getItem("th_user") ||
      localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// S7744: give opts a meaningful default shape instead of bare {}
// S4624: flatten nested template literals
function authFetch(path, opts) {
  const options = opts || {};
  const token = readToken();
  const prefix = path.startsWith("/api/") ? path : "/api" + (path.startsWith("/") ? path : "/" + path);
  return fetch(`${API_BASE}${prefix}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// S4624: flatten nested template literals
function authUpload(path, formData) {
  const token = readToken();
  const prefix = path.startsWith("/api/") ? path : "/api" + (path.startsWith("/") ? path : "/" + path);
  return fetch(`${API_BASE}${prefix}`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
}

function resolveApiUploadUrl(raw) {
  if (!raw) return "";
  if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
  const slash = raw.startsWith("/") ? raw : "/" + raw;
  return `${API_BASE}${slash}`;
}

// S7764: use globalThis instead of window
function getPublicProfileUrl(username) {
if (!username || typeof window === "undefined") return "";
return window.location.origin + "/candidate/" + username;
}

function getCandidateUsername(app) {
  const a = app || {};
  return (
    a.username ||
    a.candidate_username ||
    a.user_username ||
    a.profile_username ||
    ""
  ).trim();
}

// ─── Static config ──────────────────────────────────────────────────
const STAGES = ["Applied", "Screening", "Interview", "Offer Sent", "Hired", "Rejected"];
const APP_STATUS_CONFIG = {
  applied:     { label: "New Apply",   color: "#7c3aed", bg: "#f5f3ff" },
  pending:     { label: "Pending",     color: "#7c3aed", bg: "#f5f3ff" },
  shortlisted: { label: "Shortlisted", color: "#263012", bg: "#eef2ff" },
  interview:   { label: "Interview",   color: "#0891b2", bg: "#e0f7ff" },
  hired:       { label: "Hired",       color: "#263012", bg: "#f0fdf4" },
  rejected:    { label: "Rejected",    color: "#394420", bg: "#fef2f2" },
};
const AVATAR_COLORS = ["#4338ca","#6366f1","#394420","#16a34a","#263012","#7c3aed","#0891b2","#db2777","#0d9488"];
const NAV_ITEMS = [
  { id: "dashboard",       label: "Dashboard",        icon: "grid"      },
  { id: "profile",         label: "Company Profile",  icon: "eye"       },
  { id: "reviews",         label: "Reviews",          icon: "star"      },
  { id: "recommendations", label: "Recommendations",  icon: "star"      },
  { id: "applications",    label: "Applications",     icon: "inbox"     },
  { id: "candidates",      label: "All Candidates",   icon: "people"    },
  { id: "jobs",            label: "Job Openings",     icon: "briefcase" },
  { id: "interviews",      label: "Interviews",       icon: "calendar"  },
  { id: "pipeline",        label: "Pipeline",         icon: "pipeline"  },
  { id: "reports",         label: "Reports",          icon: "chart"     },
  { id: "complaints",      label: "Complaints",       icon: "bell"      },
  { id: "salary-slip",     label: "Salary Slip",      icon: "slip"      },
  { id: "payroll",         label: "Payroll",          icon: "payroll"   },
  { id: "EmpManage",       label: "Employee",         icon: "employee"  },
  { id: "documents",       label: "Documents",        icon: "documents" },
];
const JOB_CATEGORIES = ["Technology","Engineering","Design","Analytics","Product","Marketing","Finance","HR","Operations","Sales","Infrastructure","Other"];
const JOB_TYPES_LIST  = ["Full-time","Part-time","Contract","Internship","Freelance","Remote","Hybrid"];

// ─── Icons ──────────────────────────────────────────────────────────
const Icons = {
  grid:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,
  inbox:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  people:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  briefcase:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  calendar:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  pipeline:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  chart:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  search:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  bell:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  check:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  x:            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  mail:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  phone:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.35 2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.1 6.1l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  pin:          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  plus:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  file:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  edit:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  logout:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  download:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  star:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  clock:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  send:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  eye:          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  refresh:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  externalLink: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  whatsapp:     <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
  menu:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  filter:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  ticket:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z"/></svg>,
};

// ─── Helpers ─────────────────────────────────────────────────────────

// S6774: add PropTypes for all helper components
function Avatar({ initials, size, idx, photo }) {
  if (photo) return (
    <div style={{ width: size, height: size, minWidth: size, borderRadius: size * 0.28, overflow: "hidden", flexShrink: 0 }}>
      <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    </div>
  );
  return (
    <div className="avatar" style={{
      width: size, height: size, minWidth: size,
      background: AVATAR_COLORS[idx % AVATAR_COLORS.length],
      fontSize: size * 0.36, borderRadius: size * 0.28
    }}>{initials}</div>
  );
}
Avatar.propTypes = {
  initials: PropTypes.string.isRequired,
  size:     PropTypes.number,
  idx:      PropTypes.number,
  photo:    PropTypes.string,
};
Avatar.defaultProps = { size: 40, idx: 0, photo: null };

function AppStatusBadge({ status }) {
  const cfg = APP_STATUS_CONFIG[status] || APP_STATUS_CONFIG["pending"];
  return <span className="badge" style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.color + "33" }}>{cfg.label}</span>;
}
AppStatusBadge.propTypes = { status: PropTypes.string };
AppStatusBadge.defaultProps = { status: "pending" };

function ScoreRing({ score, size }) {
  const r = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  // S3358: extract nested ternary
  let ringColor;
  if (score >= 85) { ringColor = "#263012"; }
  else if (score >= 70) { ringColor = "#4338ca"; }
  else { ringColor = "#394420"; }

  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f0f0" strokeWidth="4"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={ringColor} strokeWidth="4"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}/>
      </svg>
      <span className="score-label" style={{ color: ringColor, fontSize: size * 0.25 }}>{score}</span>
    </div>
  );
}
ScoreRing.propTypes = { score: PropTypes.number.isRequired, size: PropTypes.number };
ScoreRing.defaultProps = { size: 50 };

function StatCard({ label, value, trend, icon, accent }) {
  return (
    <div className={`stat-card ${accent ? "stat-card--accent" : ""}`} style={accent ? { borderColor: "rgba(67,56,202,.25)" } : {}}>
      <div className="stat-card__row">
        <div className="stat-card__icon" style={{ background: accent ? "#eef2ff" : "#f8f9fc", color: accent ? "#4338ca" : "#64748b" }}>
          <span className="icon-wrap">{Icons[icon]}</span>
        </div>
        <div className="stat-card__body">
          <div className="stat-card__value" style={accent ? { color: "#4338ca" } : {}}>{value}</div>
          <div className="stat-card__label">{label}</div>
        </div>
      </div>
      {trend && <p className="stat-card__trend">{trend}</p>}
    </div>
  );
}
StatCard.propTypes = {
  label:  PropTypes.string.isRequired,
  value:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  trend:  PropTypes.string,
  icon:   PropTypes.string.isRequired,
  accent: PropTypes.bool,
};
StatCard.defaultProps = { trend: null, accent: false };

function calcMatchScore(candidateSkills, jobSkills) {
  if (!jobSkills || !candidateSkills) return 0;
  const normalize = v => {
    if (Array.isArray(v)) return v.map(s => String(s).toLowerCase().trim()).filter(Boolean);
    if (!v) return [];
    const str = String(v).trim();
    if (str.startsWith("[")) {
      try {
        const p = JSON.parse(str);
        return Array.isArray(p) ? p.map(s => String(s).toLowerCase().trim()) : [];
      } catch { /* fall through */ }
    }
    return str.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
  };
  const jobArr = normalize(jobSkills);
  const canArr = normalize(candidateSkills);
  if (!jobArr.length) return 70;
  const matched = canArr.filter(s => jobArr.some(j => j.includes(s) || s.includes(j)));
  return Math.min(100, Math.round((matched.length / jobArr.length) * 100));
}

function ContactButtons({ email, phone, name }) {
  // S7781: use replaceAll instead of replace
  const clean = (phone || "").replaceAll(/\D/g, "");
  const waMsg = encodeURIComponent(`Hi ${name || "there"}, I came across your profile on TalentHive and would like to discuss an opportunity.`);
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {phone && (
        <a href={`tel:${phone}`} className="contact-btn contact-btn--green">
          <span className="icon-wrap" style={{ width: 13, height: 13 }}>{Icons.phone}</span>
          <span className="contact-btn-label">Call</span>
        </a>
      )}
      {phone && clean.length >= 10 && (
        <a
          href={`https://wa.me/${clean.startsWith("91") ? clean : "91" + clean}?text=${waMsg}`}
          target="_blank" rel="noreferrer" className="contact-btn contact-btn--green"
        >
          <span style={{ width: 13, height: 13, display: "flex", alignItems: "center" }}>{Icons.whatsapp}</span>
          <span className="contact-btn-label">WhatsApp</span>
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}?subject=Job Opportunity&body=Hi ${name || "there"},%0D%0A%0D%0AI found your profile on TalentHive.%0D%0A%0D%0ARegards`}
          className="contact-btn contact-btn--blue"
        >
          <span className="icon-wrap" style={{ width: 13, height: 13 }}>{Icons.mail}</span>
          <span className="contact-btn-label">Email</span>
        </a>
      )}
    </div>
  );
}
ContactButtons.propTypes = {
  email: PropTypes.string,
  phone: PropTypes.string,
  name:  PropTypes.string,
};
ContactButtons.defaultProps = { email: "", phone: "", name: "" };

function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function Search({ value, onChange, placeholder }) {
  return (
    <div className="sw" style={{ flex: 1, maxWidth: 380 }}>
      {/* S6853: associate label with control via htmlFor + id */}
      <label htmlFor="search-input" style={{ display: "none" }}>{placeholder}</label>
      <input
        id="search-input"
        className="si"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
Search.propTypes = {
  value:       PropTypes.string.isRequired,
  onChange:    PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
Search.defaultProps = { placeholder: "Search…" };

function StatusChip({ status }) {
  const map = {
    visible:           { bg: "#dcfce7", color: "#16a34a" },
    pending:           { bg: "#fef3c7", color: "#263012" },
    hidden:            { bg: "#fee2e2", color: "#394420" },
    removal_requested: { bg: "#fee2e2", color: "#b91c1c" },
  };
  const key = String(status || "visible");
  const cfg = map[key] || { bg: "#f0f2f6", color: "#64748b" };
  // S7781: replaceAll
  return (
    <span className="badge" style={{ color: cfg.color, background: cfg.bg, borderColor: `${cfg.color}33`, textTransform: "capitalize" }}>
      {key.replaceAll("_", " ")}
    </span>
  );
}
StatusChip.propTypes = { status: PropTypes.string };
StatusChip.defaultProps = { status: "visible" };

function PageButtons({ page, pages, setPage }) {
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
      <button className="btn bo" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
      {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
        <button key={p} className={`btn ${p === page ? "bp" : "bo"}`} onClick={() => setPage(p)}>{p}</button>
      ))}
      <button className="btn bo" disabled={page === pages} onClick={() => setPage(p => Math.min(pages, p + 1))}>Next →</button>
    </div>
  );
}
PageButtons.propTypes = {
  page:    PropTypes.number.isRequired,
  pages:   PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
};

// ══════════════════════════════════════════════════════════════════════
// ComplaintModal
// ══════════════════════════════════════════════════════════════════════
function ComplaintModal({ candidate, onClose, onCreated }) {
  const [step, setStep]                   = useState("terms");
  const [title, setTitle]                 = useState(
    "Complaint against " + (candidate?.full_name || candidate?.candidate_name || "candidate")
  );
  const [category, setCategory]           = useState("misconduct");
  const [message, setMessage]             = useState("");
  const [proof, setProof]                 = useState(null);
  const [visibleOnProfile, setVisible]    = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");

  useEffect(() => {
    setStep("terms");
    setTitle("Complaint against " + (candidate?.full_name || candidate?.candidate_name || "candidate"));
    setCategory("misconduct");
    setMessage("");
    setProof(null);
    setVisible(true);
    setTermsAccepted(false);
    setSaving(false);
    setError("");
  }, [candidate?.id]);

  const terms = [
    "Only submit a ticket when there is a genuine workplace or conduct issue.",
    "Attach proof that supports the complaint — screenshots, letters, or documents.",
    "Keep the message factual and professional.",
    "The ticket will be visible to the candidate unless an admin hides it later.",
    "False or malicious complaints can be reviewed by admin and may be removed.",
  ];

  const submit = async e => {
    e.preventDefault();
    if (!candidate?.id) return;
    if (!termsAccepted) { setError("Please accept the ticket terms before continuing."); return; }
    if (!title.trim())  { setError("Please add a ticket title."); return; }
    if (message.trim().length < 20) { setError("Please write at least 20 characters."); return; }

    const fd = new FormData();
    fd.append("title",              title.trim());
    fd.append("category",           category);
    fd.append("message",            message.trim());
    fd.append("visible_on_profile", visibleOnProfile ? "1" : "0");
    fd.append("terms_accepted",     "1");
    if (proof) fd.append("proof", proof);

    setSaving(true); setError("");
    try {
      const res  = await authUpload(`/api/recruiter/candidates/${candidate.id}/complaints`, fd);
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data?.error || "Failed to create ticket");
      onCreated?.(data.complaint);
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to create ticket");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__title-wrap">
            <span className="icon-wrap">{Icons.ticket}</span>
            <div>
              <h2>Raise Ticket</h2>
              <p style={{ fontSize: ".73rem", color: "#6b7094", fontWeight: 400, marginTop: 2 }}>
                {candidate?.full_name || candidate?.candidate_name || "Candidate"}
              </p>
            </div>
          </div>
          {/* S6848 / S1082: use button (native interactive element) — already a button, keep role explicit */}
          <button className="icon-btn" onClick={e => { e.stopPropagation(); onClose(); }}>
            <span className="icon-wrap">{Icons.x}</span>
          </button>
        </div>

        {step === "terms" ? (
          <div className="modal__body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ padding: 16, borderRadius: 14, background: "#f2f5ea", border: "1px solid #c8d4a0" }}>
              <div style={{ fontWeight: 800, color: "#9a3412", marginBottom: 8 }}>Terms and conditions</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: "#7c2d12", lineHeight: 1.7, fontSize: 13 }}>
                {terms.map(t => <li key={t}>{t}</li>)}
              </ul>
            </div>
            {/* S6853: associate label with checkbox via htmlFor */}
            <label htmlFor="terms-checkbox" style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
              <input
                id="terms-checkbox"
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>I confirm that this complaint is accurate and I agree to the terms above.</span>
            </label>
            {error && <div className="post-job-error">⚠️ {error}</div>}
            <div className="modal__footer" style={{ padding: 0 }}>
              <button type="button" className="btn btn--ghost" onClick={e => { e.stopPropagation(); onClose(); }}>Cancel</button>
              <button type="button" className="btn btn--primary" disabled={!termsAccepted} onClick={() => setStep("form")}>
                Continue →
              </button>
            </div>
          </div>
        ) : (
          <form className="modal__body" onSubmit={submit} noValidate>
            {error && <div className="post-job-error">⚠️ {error}</div>}
            <div className="form-grid">
              <div className="form-group form-group--full">
                {/* S6853: htmlFor matches input id */}
                <label htmlFor="ticket-title">Ticket title <span className="req-star">*</span></label>
                <input id="ticket-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Complaint title" />
              </div>
              <div className="form-group">
                <label htmlFor="ticket-category">Category</label>
                <select id="ticket-category" value={category} onChange={e => setCategory(e.target.value)}>
                  <option value="misconduct">Misconduct</option>
                  <option value="harassment">Harassment</option>
                  <option value="policy_violation">Policy Violation</option>
                  <option value="fraud">Fraud</option>
                  <option value="attendance">Attendance</option>
                  <option value="performance">Performance</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="ticket-proof">Proof (optional)</label>
                <input
                  id="ticket-proof"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                  onChange={e => setProof(e.target.files?.[0] || null)}
                />
              </div>
              <div className="form-group form-group--full">
                <label htmlFor="ticket-message">Message <span className="req-star">*</span></label>
                <textarea
                  id="ticket-message"
                  rows={6}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Describe the issue with details that can be reviewed later."
                />
              </div>
              <div className="form-group form-group--full">
                <label htmlFor="ticket-visible" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input
                    id="ticket-visible"
                    type="checkbox"
                    checked={visibleOnProfile}
                    onChange={e => setVisible(e.target.checked)}
                  />
                  Show on candidate profile until admin resolves it
                </label>
                <p style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                  You can delete your own ticket. Admin can hide, update, or fully remove it.
                </p>
              </div>
            </div>
            <div className="modal__footer">
              <button type="button" className="btn btn--ghost" onClick={() => setStep("terms")}>← Back</button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? "Saving…" : "Save ticket →"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
ComplaintModal.propTypes = {
  candidate:  PropTypes.shape({
    id:             PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name:      PropTypes.string,
    candidate_name: PropTypes.string,
  }).isRequired,
  onClose:    PropTypes.func,
  onCreated:  PropTypes.func,
};
ComplaintModal.defaultProps = { onClose: null, onCreated: null };

// ──────────────────────────────────────────────────────────────────────
// CandidateDrawer
// ──────────────────────────────────────────────────────────────────────
function CandidateDrawer({ app, jobSkills, onClose, onDecision, isCandidate, onRaiseTicket }) {
  const [deciding, setDeciding]                 = useState(false);
  // S6754: properly destructure useState
  const [note, setNote]                         = useState(app.recruiter_note || "");
  const [tab, setTab]                           = useState("profile");
  const [tickets, setTickets]                   = useState([]);
  const [ticketsLoading, setTicketsLoading]     = useState(false);
  const [ticketsError, setTicketsError]         = useState("");
  const [candidateLastActive, setCandidateLastActive] = useState(app.last_active_at || null);

  const name     = (app.candidate_name || app.full_name || app.user_name || ((app.first_name || "") + " " + (app.last_name || "")).trim() || "Candidate").trim();
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
  const email    = app.candidate_email || app.email || app.user_email || "";
  const phone    = app.user_phone || app.mobile || app.candidate_phone || "";
  const photo    = app.candidate_photo || app.profile_photo || null;
  const username  = getCandidateUsername(app);
  const publicUrl = getPublicProfileUrl(username);
  const skills = (() => {
    const s = app.user_skills || app.skills || app.candidate_skills || "";
    if (Array.isArray(s)) return s;
    try { const p = JSON.parse(String(s)); if (Array.isArray(p)) return p; } catch { /* fall through */ }
    return String(s || "").split(",").map(x => x.trim()).filter(Boolean);
  })();
  const jSkills    = app.job_skills || jobSkills || "";
  const matchScore = isCandidate ? null : calcMatchScore(skills, jSkills);
  const locations  = Array.isArray(app.user_locations || app.locations) ? (app.user_locations || app.locations) : [];
  const daysAgo    = app.applied_at ? Math.max(0, Math.floor((Date.now() - new Date(app.applied_at)) / 86400000)) : null;
  const jobArr     = jSkills ? (typeof jSkills === "string" ? jSkills.split(",").map(s => s.trim().toLowerCase()) : []) : [];
  const candidateId = Number(app.candidate_id || app.id || 0);

  useEffect(() => {
    setCandidateLastActive(app.last_active_at || null);
    if (isCandidate || !candidateId) return;
    let alive = true;
    (async () => {
      setTicketsLoading(true);
      setTicketsError("");
      try {
        const res  = await authFetch(`/api/recruiter/candidates/${candidateId}/complaints`);
        const data = await readJsonResponse(res);
        if (!res.ok) throw new Error(data?.error || "Failed to load tickets");
        if (!alive) return;
        setTickets(Array.isArray(data.complaints) ? data.complaints : []);
        setCandidateLastActive(data?.candidate?.last_active_at || app.last_active_at || null);
      } catch (err) {
        if (!alive) return;
        setTickets([]);
        setTicketsError(err.message || "Failed to load tickets");
      } finally {
        if (alive) setTicketsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [candidateId, isCandidate, app.last_active_at]);

  const decide = async decision => {
    if (isCandidate) return;
    setDeciding(true);
    try {
      await authFetch(`/applications/${app.id}/decision`, { method: "PUT", body: JSON.stringify({ status: decision, note }) });
      if (onDecision) onDecision(app.id, decision, note);
      onClose();
    } catch {
      if (onDecision) onDecision(app.id, decision, note);
      onClose();
    } finally {
      setDeciding(false);
    }
  };

  const tabs = isCandidate ? ["profile", "contact"] : ["profile", "contact", "tickets", "notes", "actions"];

  // S3358: extract nested ternaries for daysAgo label
  let daysAgoLabel = "";
  if (daysAgo !== null) {
    if (daysAgo === 0) { daysAgoLabel = "Today"; }
    else if (daysAgo === 1) { daysAgoLabel = "Yesterday"; }
    else { daysAgoLabel = daysAgo + "d ago"; }
  }

  // S3358: extract match score badge styles
  let matchBg = "#fef2f2", matchColor = "#394420", matchBorder = "#fca5a5";
  if (matchScore !== null) {
    if (matchScore >= 85) { matchBg = "#f0fdf4"; matchColor = "#16a34a"; matchBorder = "#86efac"; }
    else if (matchScore >= 70) { matchBg = "#eef2ff"; matchColor = "#4338ca"; matchBorder = "#c7d2fe"; }
  }

  // S3358: extract score ring label
  let scoreLabel = "Below Average";
  if (matchScore !== null) {
    if (matchScore >= 85) { scoreLabel = "Excellent Fit"; }
    else if (matchScore >= 70) { scoreLabel = "Good Fit"; }
  }
  let scoreLabelColor = "#394420";
  if (matchScore !== null) {
    if (matchScore >= 85) { scoreLabelColor = "#263012"; }
    else if (matchScore >= 70) { scoreLabelColor = "#4338ca"; }
  }

  return (
    /* S6848 / S1082: drawer-overlay uses role + keyboard handler */
    <div
      className="drawer-overlay"
      role="presentation"
      onClick={onClose}
      onKeyDown={e => { if (e.key === "Escape") onClose(); }}
    >
      <div className="drawer" onClick={e => e.stopPropagation()}>
        <div className="drawer__header">
          <div className="drawer__hero">
            <Avatar initials={initials} size={50} idx={0} photo={photo} />
            <div className="drawer__hero-info">
              <h2>{name}</h2>
              {!isCandidate && <p>{app.job_title || "Applied Position"}{app.company_name ? " · " + app.company_name : ""}</p>}
              {isCandidate && (app.headline || app.user_headline) && <p>{app.headline || app.user_headline}</p>}
              <div className="drawer__hero-meta">
                {!isCandidate && <AppStatusBadge status={app.status || "pending"} />}
                {isCandidate && (
                  <span style={{ padding: "2px 9px", borderRadius: 99, fontSize: 11, fontWeight: 700, background: "#f0fdf4", color: "#16a34a", border: "1px solid #86efac" }}>
                    {app.application_count || 0} apps
                  </span>
                )}
                {daysAgo !== null && (
                  <span style={{ fontSize: ".73rem", color: "#64748b" }}>{daysAgoLabel}</span>
                )}
                {matchScore !== null && (
                  <span style={{
                    padding: "2px 9px", borderRadius: 99, fontSize: 11, fontWeight: 800,
                    background: matchBg, color: matchColor, border: `1px solid ${matchBorder}`
                  }}>
                    {matchScore}% match
                  </span>
                )}
              </div>

              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {publicUrl && (
                  <a href={publicUrl} target="_blank" rel="noreferrer" className="btn btn--outline btn--sm">
                    Open public profile
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn--sm"
                  style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", fontWeight: 700 }}
                  onClick={e => {
                    e.stopPropagation();
                    onClose();
                    onRaiseTicket?.({ id: app.id || app.candidate_id, full_name: name, candidate_name: name });
                  }}
                >
                  🎫 Raise Ticket
                </button>
              </div>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><span className="icon-wrap">{Icons.x}</span></button>
        </div>

        <div className="drawer__tabs">
          {tabs.map(t => (
            <button key={t} className={`dtab ${tab === t ? "dtab--active" : ""}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div className="drawer__body">
          {tab === "profile" && (
            <div className="dtab-content">
              <div className="info-list">
                {(app.headline || app.user_headline) && <div className="info-item"><span>💼</span><span style={{ fontStyle: "italic", color: "#374151" }}>{app.headline || app.user_headline}</span></div>}
                {email && <div className="info-item"><span className="icon-wrap">{Icons.mail}</span><span>{email}</span></div>}
                {phone && <div className="info-item"><span className="icon-wrap">{Icons.phone}</span><span>{phone}</span></div>}
                {locations.length > 0 && <div className="info-item"><span className="icon-wrap">{Icons.pin}</span><span>{locations.join(", ")}</span></div>}
                {(app.user_experience || app.work_status) && <div className="info-item"><span className="icon-wrap">{Icons.clock}</span><span>{app.user_experience || app.work_status}</span></div>}
                {!isCandidate && candidateLastActive && <div className="info-item"><span className="icon-wrap">{Icons.bell}</span><span>Last active: {fmtDate(candidateLastActive)}</span></div>}
                {app.salary && <div className="info-item"><span>💰</span><span>Expected: ₹{app.salary}/yr</span></div>}
              </div>

              {matchScore !== null && (
                <div className="drawer-section">
                  <div className="section-title">Match Score</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <ScoreRing score={matchScore} size={50} />
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 14, color: scoreLabelColor }}>{scoreLabel}</p>
                      <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>Based on required skills</p>
                    </div>
                  </div>
                </div>
              )}

              {skills.length > 0 && (
                <div className="drawer-section">
                  <div className="section-title">Skills</div>
                  <div className="skills-wrap">
                    {skills.map(s => {
                      const matched = jobArr.some(j => j.includes(s.toLowerCase()) || s.toLowerCase().includes(j));
                      return (
                        <span key={s} className="skill-tag" style={{
                          background: matched ? "#f0fdf4" : "#f8fafc",
                          border:     matched ? "1.5px solid #86efac" : "1.5px solid #e2e8f0",
                          color:      matched ? "#16a34a" : "#374151",
                        }}>
                          {matched ? "✓ " : ""}{s}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {(app.resume_url || app.user_resume) && (
                <div className="drawer-section">
                  <a
                    href={app.resume_url || app.user_resume}
                    target="_blank" rel="noreferrer"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 16px", background: "#2563eb", color: "white", borderRadius: 9, textDecoration: "none", fontWeight: 700, fontSize: 13 }}
                  >
                    <span className="icon-wrap">{Icons.download}</span> View / Download Resume
                  </a>
                </div>
              )}

              {!isCandidate && app.job_title && (
                <div className="drawer-section">
                  <div className="section-title">Applied For</div>
                  <div className="app-job-card">
                    <div className="app-job-card__title">{app.job_title}</div>
                    <div className="app-job-card__company">{app.company_name || app.company || "—"}</div>
                    {app.job_location && <div className="app-job-card__meta">📍 {app.job_location}</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "contact" && (
            <div className="dtab-content">
              <div style={{ padding: "14px", background: "#f0fdf4", borderRadius: 11, border: "1.5px solid #86efac", marginBottom: 14 }}>
                <p style={{ fontWeight: 800, fontSize: 14, color: "#16a34a", marginBottom: 8 }}>📞 Contact {name.split(" ")[0]}</p>
                <ContactButtons email={email} phone={phone} name={name.split(" ")[0]} />
              </div>
              {phone && (
                <div className="drawer-section">
                  <div className="section-title">Phone</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: "#f8fafc", borderRadius: 9, border: "1.5px solid #e2e8f0" }}>
                    <span style={{ fontSize: 18 }}>📱</span>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{phone}</span>
                    <a href={`tel:${phone}`} style={{ marginLeft: "auto", padding: "5px 11px", background: "#16a34a", color: "white", borderRadius: 7, textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Call</a>
                  </div>
                </div>
              )}
              {email && (
                <div className="drawer-section">
                  <div className="section-title">Email</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: "#f8fafc", borderRadius: 9, border: "1.5px solid #e2e8f0", wordBreak: "break-all" }}>
                    <span style={{ fontSize: 18 }}>✉️</span>
                    <span style={{ fontWeight: 700, fontSize: 12, flex: 1 }}>{email}</span>
                    <a href={`mailto:${email}`} style={{ flexShrink: 0, padding: "5px 11px", background: "#2563eb", color: "white", borderRadius: 7, textDecoration: "none", fontSize: 12, fontWeight: 700 }}>Email</a>
                  </div>
                </div>
              )}
              <div className="drawer-section">
                <div className="section-title">Quick Templates</div>
                {[
                  { label: "Interview Invite",     subject: "Interview Invitation",  body: "Hi " + name.split(" ")[0] + ",%0D%0A%0D%0AWe'd like to invite you for an interview. Please share your availability.%0D%0A%0D%0ARegards" },
                  { label: "Opportunity Outreach", subject: "Exciting Opportunity",  body: "Hi " + name.split(" ")[0] + ",%0D%0A%0D%0AI came across your profile and we have an exciting opening.%0D%0A%0D%0ARegards" },
                ].map(tpl => (
                  <a
                    key={tpl.label}
                    href={`mailto:${email}?subject=${encodeURIComponent(tpl.subject)}&body=${tpl.body}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: "white", border: "1.5px solid #e2e8f0", borderRadius: 9, textDecoration: "none", marginBottom: 7, transition: "all .14s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.background = "#f5f3ff"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background  = "white"; }}
                  >
                    <span style={{ fontSize: 18 }}>📧</span>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{tpl.label}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "#6366f1", fontWeight: 700 }}>Send →</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {tab === "tickets" && !isCandidate && (
            <div className="dtab-content">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
                <div>
                  <div className="section-title">All Complaint Tickets</div>
                  <p style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>Every ticket for this candidate, including resolved and hidden records.</p>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span className="skill-tag sm" style={{ background: "#f8fafc" }}>{tickets.length} total</span>
                  <span className="skill-tag sm" style={{ background: "#f2f5ea", color: "#b45309" }}>{tickets.filter(t => ["open", "pending"].includes(String(t.status || "").toLowerCase())).length} open</span>
                  <span className="skill-tag sm" style={{ background: "#f0fdf4", color: "#16a34a" }}>{tickets.filter(t => String(t.status || "").toLowerCase() === "resolved").length} resolved</span>
                </div>
              </div>
              {ticketsLoading && <div style={{ padding: 20, color: "#94a3b8" }}>Loading tickets…</div>}
              {ticketsError  && <div className="post-job-error">{ticketsError}</div>}
              {!ticketsLoading && !ticketsError && tickets.length === 0 && (
                <div style={{ padding: 18, borderRadius: 12, background: "#f8fafc", color: "#64748b" }}>No complaint tickets found for this candidate.</div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {tickets.map(ticket => {
                  // S3358: extract ticket status badge styles
                  let tBg = "#fef3c7", tColor = "#263012";
                  if (ticket.status === "resolved") { tBg = "#dcfce7"; tColor = "#16a34a"; }
                  else if (ticket.status === "hidden") { tBg = "#fee2e2"; tColor = "#dc2626"; }
                  // S7781: replaceAll
                  const ticketStatusLabel = String(ticket.status || "open").replaceAll("_", " ");
                  return (
                    <div key={ticket.id} style={{ padding: 14, borderRadius: 14, border: "1.5px solid #e2e8f0", background: "#fff" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 14 }}>{ticket.title}</div>
                          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                            {ticket.recruiter_company || ticket.recruiter_name || "Recruiter"} · {fmtDate(ticket.created_at)}
                          </div>
                        </div>
                        <span style={{ padding: "4px 9px", borderRadius: 99, fontSize: 11, fontWeight: 800, background: tBg, color: tColor }}>
                          {ticketStatusLabel}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{ticket.message || "No message provided."}</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                        {/* S7781: replaceAll */}
                        <span className="skill-tag sm" style={{ background: "#f8fafc" }}>{String(ticket.category || "misconduct").replaceAll("_", " ")}</span>
                        <span className="skill-tag sm" style={{ background: ticket.visible_on_profile ? "#dcfce7" : "#fee2e2", color: ticket.visible_on_profile ? "#16a34a" : "#dc2626" }}>{ticket.visible_on_profile ? "Visible on profile" : "Hidden from profile"}</span>
                        {ticket.proof_url && <a href={ticket.proof_url} target="_blank" rel="noreferrer" className="skill-tag sm" style={{ textDecoration: "none", background: "#eef2ff", color: "#4338ca" }}>View proof</a>}
                      </div>
                      {(ticket.hidden_reason || ticket.admin_note) && (
                        <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                          {ticket.hidden_reason && <div style={{ padding: 10, borderRadius: 10, background: "#f2f5ea", border: "1px solid #c8d4a0", fontSize: 12.5 }}><strong style={{ display: "block", marginBottom: 3 }}>Hidden reason</strong>{ticket.hidden_reason}</div>}
                          {ticket.admin_note    && <div style={{ padding: 10, borderRadius: 10, background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12.5 }}><strong style={{ display: "block", marginBottom: 3 }}>Admin note</strong>{ticket.admin_note}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "notes" && !isCandidate && (
            <div className="dtab-content">
              <p style={{ fontSize: 12.5, color: "#64748b", marginBottom: 10 }}>Private notes visible only to your team</p>
              <textarea
                className="notes-area"
                placeholder="Add notes about this candidate…"
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={8}
              />
              <button
                style={{ marginTop: 10, width: "100%", padding: "10px", background: "#4338ca", color: "white", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
                onClick={() => authFetch(`/applications/${app.id}/decision`, { method: "PUT", body: JSON.stringify({ status: app.status || "pending", note }) })}
              >
                ✓ Save Notes
              </button>
            </div>
          )}

          {tab === "actions" && !isCandidate && (
            <div className="dtab-content">
              <div className="action-grid">
                <button className="action-btn action-btn--green"  onClick={() => decide("hired")}       disabled={deciding}><span className="action-icon icon-wrap">{Icons.check}</span>    <span className="action-label">Hire</span>          <span className="action-sub">Mark as hired</span></button>
                <button className="action-btn action-btn--blue"   onClick={() => decide("shortlisted")} disabled={deciding}><span className="action-icon icon-wrap">{Icons.star}</span>     <span className="action-label">Shortlist</span>     <span className="action-sub">Move to shortlist</span></button>
                <button className="action-btn action-btn--purple" onClick={() => decide("interview")}   disabled={deciding}><span className="action-icon icon-wrap">{Icons.calendar}</span> <span className="action-label">Interview</span>    <span className="action-sub">Schedule interview</span></button>
                <button className="action-btn action-btn--orange" onClick={() => decide("pending")}     disabled={deciding}><span className="action-icon icon-wrap">{Icons.clock}</span>    <span className="action-label">Keep Pending</span> <span className="action-sub">Review later</span></button>
                <button className="action-btn action-btn--red full-span" onClick={() => decide("rejected")} disabled={deciding}><span className="action-icon icon-wrap">{Icons.x}</span>  <span className="action-label">Reject</span>        <span className="action-sub">Remove from pipeline</span></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
CandidateDrawer.propTypes = {
  app:          PropTypes.object.isRequired,
  jobSkills:    PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onClose:      PropTypes.func.isRequired,
  onDecision:   PropTypes.func,
  isCandidate:  PropTypes.bool,
  onRaiseTicket: PropTypes.func,
};
CandidateDrawer.defaultProps = {
  jobSkills:    "",
  onDecision:   null,
  isCandidate:  false,
  onRaiseTicket: null,
};

// ─── Post Job Modal ──────────────────────────────────────────────────
function PostJobModal({ onClose, onJobPosted, employerId, companyName }) {
  const [form, setForm]       = useState({ title: "", category: "Technology", type: "Full-time", location: "", experience: "", description: "", skills: "", min_salary: "", max_salary: "", openings: "1", expires_date: "", urgent: false, featured: false });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim())    return setError("Job title is required.");
    if (!form.location.trim()) return setError("Location is required.");
    setError(""); setLoading(true);
    try {
      const res = await authFetch("/api/employer/jobs", {
        method: "POST", body: JSON.stringify({
          title:       form.title.trim(),
          company:     companyName || "Our Company",
          employer_id: employerId || null,
          location:    form.location.trim(),
          type:        form.type,
          category:    form.category,
          description: form.description.trim(),
          skills:      form.skills.trim(),
          experience:  form.experience.trim(),
          min_salary:  form.min_salary ? Number(form.min_salary) * 100000 : null,
          max_salary:  form.max_salary ? Number(form.max_salary) * 100000 : null,
          openings:    Number(form.openings) || 1,
          urgent:      form.urgent,
          featured:    form.featured,
          expires_date: form.expires_date || null,
        })
      });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
      setSuccess(true);
      setTimeout(() => {
        onJobPosted({
          id:         data.job_id,
          title:      form.title.trim(),
          dept:       form.category,
          applicants: 0,
          deadline:   form.expires_date
            ? new Date(form.expires_date).toLocaleDateString("en-IN", { month: "short", day: "numeric" })
            : "—",
          status: "Active",
          type:   form.type,
        });
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.message || "Failed to post job.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__title-wrap">
            <span className="icon-wrap">{Icons.briefcase}</span>
            <div>
              <h2>Post a New Job</h2>
              <p style={{ fontSize: ".73rem", color: "#6b7094", fontWeight: 400, marginTop: 2 }}>Goes live immediately on the Jobs page</p>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><span className="icon-wrap">{Icons.x}</span></button>
        </div>
        {success ? (
          <div className="modal__body" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 14 }}>
            <div style={{ fontSize: "3rem" }}>🎉</div>
            <p style={{ fontWeight: 800, fontSize: "1.1rem", color: "#263012" }}>Job Posted Successfully!</p>
            <p style={{ color: "#94a3b8", fontSize: ".84rem" }}>Candidates can now find and apply.</p>
          </div>
        ) : (
          <form className="modal__body" onSubmit={handleSubmit} noValidate>
            {error && <div className="post-job-error">⚠️ {error}</div>}
            <div className="form-grid">
              {/* S6853: all labels have htmlFor matching input id */}
              <div className="form-group form-group--full">
                <label htmlFor="pj-title">Job Title <span className="req-star">*</span></label>
                <input id="pj-title" type="text" placeholder="e.g. Senior React Developer" value={form.title} onChange={e => set("title", e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pj-category">Category</label>
                <select id="pj-category" value={form.category} onChange={e => set("category", e.target.value)}>{JOB_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
              </div>
              <div className="form-group">
                <label htmlFor="pj-type">Job Type</label>
                <select id="pj-type" value={form.type} onChange={e => set("type", e.target.value)}>{JOB_TYPES_LIST.map(t => <option key={t}>{t}</option>)}</select>
              </div>
              <div className="form-group">
                <label htmlFor="pj-location">Location <span className="req-star">*</span></label>
                <input id="pj-location" type="text" placeholder="e.g. Bangalore / Remote" value={form.location} onChange={e => set("location", e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pj-experience">Experience Required</label>
                <input id="pj-experience" type="text" placeholder="e.g. 3–5 years" value={form.experience} onChange={e => set("experience", e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pj-min-salary">Min Salary (LPA)</label>
                <input id="pj-min-salary" type="number" placeholder="10" min="0" value={form.min_salary} onChange={e => set("min_salary", e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pj-max-salary">Max Salary (LPA)</label>
                <input id="pj-max-salary" type="number" placeholder="18" min="0" value={form.max_salary} onChange={e => set("max_salary", e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pj-openings">No. of Openings</label>
                <input id="pj-openings" type="number" placeholder="1" min="1" value={form.openings} onChange={e => set("openings", e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pj-deadline">Application Deadline</label>
                <input id="pj-deadline" type="date" min={new Date().toISOString().split("T")[0]} value={form.expires_date} onChange={e => set("expires_date", e.target.value)} />
              </div>
              <div className="form-group form-group--full">
                <label htmlFor="pj-skills">Required Skills (comma-separated)</label>
                <input id="pj-skills" type="text" placeholder="e.g. React, TypeScript, Node.js" value={form.skills} onChange={e => set("skills", e.target.value)} />
              </div>
              <div className="form-group form-group--full">
                <label htmlFor="pj-desc">Job Description</label>
                <textarea id="pj-desc" rows={5} placeholder="Describe the role, responsibilities, and requirements…" value={form.description} onChange={e => set("description", e.target.value)} />
              </div>
              <div className="form-group form-group--full">
                <div className="post-job-flags">
                  <label htmlFor="pj-urgent" className="flag-toggle">
                    <input id="pj-urgent" type="checkbox" checked={form.urgent} onChange={e => set("urgent", e.target.checked)} />
                    <span className="flag-label">🔥 Mark as Urgent</span>
                    <span className="flag-sub">Urgent badge on listing</span>
                  </label>
                  <label htmlFor="pj-featured" className="flag-toggle">
                    <input id="pj-featured" type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} />
                    <span className="flag-label">⭐ Feature this job</span>
                    <span className="flag-sub">Pinned at top</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal__footer">
              <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={loading}>
                {loading
                  ? <><span className="btn-spinner" />Posting…</>
                  : <><span className="icon-wrap">{Icons.plus}</span> Post Job — Go Live</>
                }
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
PostJobModal.propTypes = {
  onClose:     PropTypes.func.isRequired,
  onJobPosted: PropTypes.func.isRequired,
  employerId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyName: PropTypes.string,
};
PostJobModal.defaultProps = { employerId: null, companyName: "" };

// ══════════════════════════════════════════════════════════════════════
// ApplicationsView
// ══════════════════════════════════════════════════════════════════════
function ApplicationsView({ onNavTo, onRaiseTicket }) {
  const [applications, setApplications] = useState([]);
  const [jobsMap,      setJobsMap]      = useState({});
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [selectedApp,  setSelectedApp]  = useState(null);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter,    setJobFilter]    = useState("all");
  const [smartFilter,  setSmartFilter]  = useState("all");

  const fetchApplications = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [appRes, jobRes] = await Promise.all([authFetch("/api/applications/recruiter"), authFetch("/api/employer/profile")]);
      const [appData, jobData] = await Promise.all([readJsonResponse(appRes), readJsonResponse(jobRes)]);
      if (!appRes.ok) throw new Error(appData.error || `Server ${appRes.status}`);
      const list = Array.isArray(appData) ? appData : (appData.data || appData.applications || []);
      const jobs = Array.isArray(jobData) ? jobData : (jobData.data || jobData.jobs || []);
      const jm = {};
      jobs.forEach(j => { jm[j.id] = j.skills || ""; });
      setJobsMap(jm);
      setApplications(list.map(app => ({
        ...app,
        matchScore: calcMatchScore(app.user_skills || app.candidate_skills || "", app.job_skills || jm[app.job_id] || ""),
        daysAgo: app.applied_at ? Math.floor((Date.now() - new Date(app.applied_at)) / 86400000) : 999,
      })));
    } catch (e) {
      setError(e.message || "Could not load applications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const handleDecision = useCallback((appId, decision, note) => {
    setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: decision, recruiter_note: note } : a));
  }, []);

  const quickDecide = useCallback(async (appId, decision) => {
    try { await authFetch(`/applications/${appId}/decision`, { method: "PUT", body: JSON.stringify({ status: decision, note: "" }) }); } catch { /* ignore */ }
    handleDecision(appId, decision, "");
  }, [handleDecision]);

  const jobTitles = useMemo(() => [...new Set(applications.map(a => a.job_title).filter(Boolean))], [applications]);

  const counts = useMemo(() => ({
    all:         applications.length,
    pending:     applications.filter(a => !a.status || a.status === "pending" || a.status === "applied").length,
    shortlisted: applications.filter(a => a.status === "shortlisted").length,
    interview:   applications.filter(a => a.status === "interview").length,
    hired:       applications.filter(a => a.status === "hired").length,
    rejected:    applications.filter(a => a.status === "rejected").length,
  }), [applications]);

  const filtered = useMemo(() => {
    let list = [...applications];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        (a.candidate_name || "").toLowerCase().includes(q) ||
        (a.candidate_email || "").toLowerCase().includes(q) ||
        (a.job_title || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      list = list.filter(a =>
        statusFilter === "pending"
          ? (!a.status || a.status === "pending" || a.status === "applied")
          : a.status === statusFilter
      );
    }
    if (jobFilter !== "all") list = list.filter(a => a.job_title === jobFilter);
    if (smartFilter === "top_match") list = [...list].sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
    if (smartFilter === "perfect")   list = list.filter(a => (a.matchScore || 0) === 100);
    if (smartFilter === "latest")    list = [...list].sort((a, b) => new Date(b.applied_at || 0) - new Date(a.applied_at || 0));
    if (smartFilter === "new")       list = list.filter(a => (a.daysAgo || 999) <= 2);
    return list;
  }, [applications, search, statusFilter, jobFilter, smartFilter]);

  const daysAgoStr = dt => {
    if (!dt) return "";
    const d = Math.max(0, Math.floor((Date.now() - new Date(dt)) / 86400000));
    if (d === 0) return "Today";
    if (d === 1) return "Yesterday";
    return d + "d ago";
  };

  if (loading) return (
    <div className="view-anim" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 14, color: "#94a3b8" }}>
      <div className="spinner" />Loading applications…
    </div>
  );
  if (error) return (
    <div className="view-anim" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 60, color: "#94a3b8", textAlign: "center" }}>
      <span style={{ fontSize: "2rem" }}>⚠️</span>
      <p>{error}</p>
      <button className="btn btn--outline" onClick={fetchApplications}><span className="icon-wrap">{Icons.refresh}</span> Retry</button>
    </div>
  );

  return (
    <div className="view-anim">
      <div className="page-hero">
        <div>
          <h1 className="page-h1">Applications Inbox</h1>
          <p className="page-sub">{applications.length} total · {counts.pending} pending review</p>
        </div>
        <button className="btn btn--outline" onClick={fetchApplications}>
          <span className="icon-wrap">{Icons.refresh}</span> <span className="hide-xs">Refresh</span>
        </button>
      </div>

      <div className="app-summary-pills">
        {Object.entries(counts).map(([k, v]) => {
          const cfg = k === "all" ? { color: "#374151", bg: "#f3f4f6" } : APP_STATUS_CONFIG[k] || { color: "#64748b", bg: "#f8fafc" };
          return (
            <button
              key={k}
              className={`app-pill ${statusFilter === k ? "app-pill--on" : ""}`}
              style={statusFilter === k ? { background: cfg.bg, color: cfg.color, borderColor: cfg.color } : {}}
              onClick={() => setStatusFilter(k)}
            >
              {k === "all" ? "All" : APP_STATUS_CONFIG[k]?.label || k}
              <span className="app-pill__count">{v}</span>
            </button>
          );
        })}
      </div>

      <div className="filters-panel">
        <div className="smart-filters">
          {[{ id: "all", label: "🔍 All" }, { id: "latest", label: "🆕 Latest" }, { id: "new", label: "⚡ New (48h)" }, { id: "top_match", label: "🎯 Top Match" }, { id: "perfect", label: "✅ 100% Match" }].map(f => (
            <button key={f.id} onClick={() => setSmartFilter(f.id)} className={`smart-chip ${smartFilter === f.id ? "smart-chip--on" : ""}`}>{f.label}</button>
          ))}
        </div>
        <div className="toolbar">
          <div className="recruiter-search-box">
            <span className="icon-wrap">{Icons.search}</span>
            <input placeholder="Search candidate, job, email…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="clear-btn" onClick={() => setSearch("")}><span className="icon-wrap">{Icons.x}</span></button>}
          </div>
          <select className="filter-select" value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
            <option value="all">All Jobs</option>
            {jobTitles.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <span style={{ fontSize: "2.4rem" }}>📭</span>
          <p>{applications.length === 0 ? "No applications yet" : "No applications match your filters"}</p>
        </div>
      ) : (
        <>
          <div className="table-wrap desktop-table">
            <table className="c-table">
              <thead><tr><th>Candidate</th><th>Applied For</th><th>Applied</th><th>Match</th><th>Status</th><th>Contact</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map((app, i) => {
                  const name     = app.candidate_name || app.user_name || "Unknown";
                  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const score    = app.matchScore || 0;
                  // S3358: extract score chip styles
                  let sBg = "#fef2f2", sColor = "#394420", sBorder = "#fca5a5";
                  if (score >= 85) { sBg = "#f0fdf4"; sColor = "#16a34a"; sBorder = "#86efac"; }
                  else if (score >= 70) { sBg = "#eef2ff"; sColor = "#4338ca"; sBorder = "#c7d2fe"; }
                  return (
                    <tr key={app.id} className="c-row" onClick={() => setSelectedApp(app)}>
                      <td><div className="c-cell"><Avatar initials={initials} size={34} idx={i} photo={app.candidate_photo || null} /><div><p className="c-name">{name}</p><p className="c-email">{app.candidate_email || "—"}</p></div></div></td>
                      <td><p style={{ fontWeight: 700, fontSize: ".82rem", color: "#0d0f1a" }}>{app.job_title || "—"}</p><p style={{ fontSize: ".71rem", color: "#94a3b8" }}>{app.company_name || ""}</p></td>
                      <td className="date-td">{daysAgoStr(app.applied_at)}</td>
                      <td>
                        <span className="score-chip" style={{ background: sBg, color: sColor, border: `1px solid ${sBorder}` }}>
                          {score}%{score === 100 ? " ✅" : ""}
                        </span>
                      </td>
                      <td><AppStatusBadge status={app.status || "pending"} /></td>
                      <td onClick={e => e.stopPropagation()}><ContactButtons email={app.candidate_email || ""} phone={app.user_phone || ""} name={(app.candidate_name || "").split(" ")[0]} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="c-actions">
                          <button className="icon-btn sm" title="View" onClick={() => setSelectedApp(app)}><span className="icon-wrap">{Icons.eye}</span></button>
                          <button className="icon-btn sm" title="Raise Ticket" style={{ color: "#263012" }} onClick={() => onRaiseTicket?.({ id: app.id, full_name: app.candidate_name || "Candidate", candidate_name: app.candidate_name || "Candidate" })}><span className="icon-wrap">{Icons.ticket}</span></button>
                          <button className="icon-btn sm" title="Shortlist" style={{ color: "#4338ca" }} onClick={() => quickDecide(app.id, "shortlisted")}><span className="icon-wrap">{Icons.star}</span></button>
                          <button className="icon-btn sm" title="Hire" style={{ color: "#263012" }} onClick={() => quickDecide(app.id, "hired")}><span className="icon-wrap">{Icons.check}</span></button>
                          <button className="icon-btn sm" title="Reject" style={{ color: "#394420" }} onClick={() => quickDecide(app.id, "rejected")}><span className="icon-wrap">{Icons.x}</span></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mobile-cards">
            {filtered.map((app, i) => {
              const name     = app.candidate_name || app.user_name || "Unknown";
              const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
              const score    = app.matchScore || 0;
              // S3358: extract score chip styles
              let sBg = "#fef2f2", sColor = "#394420", sBorder = "#fca5a5";
              if (score >= 85) { sBg = "#f0fdf4"; sColor = "#16a34a"; sBorder = "#86efac"; }
              else if (score >= 70) { sBg = "#eef2ff"; sColor = "#4338ca"; sBorder = "#c7d2fe"; }
              return (
                <div key={app.id} className="mobile-card" onClick={() => setSelectedApp(app)}>
                  <div className="mc-header">
                    <Avatar initials={initials} size={40} idx={i} photo={app.candidate_photo || null} />
                    <div className="mc-info"><p className="mc-name">{name}</p><p className="mc-sub">{app.job_title || "—"}</p></div>
                    <AppStatusBadge status={app.status || "pending"} />
                  </div>
                  <div className="mc-row">
                    <span className="score-chip" style={{ background: sBg, color: sColor, border: `1px solid ${sBorder}` }}>{score}% match</span>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>{daysAgoStr(app.applied_at)}</span>
                  </div>
                  <div onClick={e => e.stopPropagation()}><ContactButtons email={app.candidate_email || ""} phone={app.user_phone || ""} name={name.split(" ")[0]} /></div>
                  <div className="mc-actions" onClick={e => e.stopPropagation()}>
                    <button className="mc-btn" style={{ background: "#fef3c7", color: "#92400e" }} onClick={() => onRaiseTicket?.({ id: app.id, full_name: name, candidate_name: name })}>🎫 Ticket</button>
                    <button className="mc-btn mc-btn--blue"  onClick={() => quickDecide(app.id, "shortlisted")}>Shortlist</button>
                    <button className="mc-btn mc-btn--green" onClick={() => quickDecide(app.id, "hired")}>Hire</button>
                    <button className="mc-btn mc-btn--red"   onClick={() => quickDecide(app.id, "rejected")}>Reject</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {selectedApp && (
        <CandidateDrawer
          app={selectedApp}
          jobSkills={jobsMap[selectedApp.job_id] || ""}
          onClose={() => setSelectedApp(null)}
          onDecision={(id, d, n) => { handleDecision(id, d, n); setSelectedApp(null); }}
          onRaiseTicket={onRaiseTicket}
        />
      )}
    </div>
  );
}
ApplicationsView.propTypes = {
  onNavTo:      PropTypes.func.isRequired,
  onRaiseTicket: PropTypes.func.isRequired,
};

// ══════════════════════════════════════════════════════════════════════
// CandidatesView
// ══════════════════════════════════════════════════════════════════════
function CandidatesView({ onNavTo, onRaiseTicket }) {
  const [candidates,   setCandidates]   = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [pages,        setPages]        = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState("");
  const [searchInput,  setSearchInput]  = useState("");
  const [skillFilter,  setSkillFilter]  = useState("");
  const [workStatus,   setWorkStatus]   = useState("");
  const [selected,     setSelected]     = useState(null);
  const [showFilters,  setShowFilters]  = useState(false);
  const searchTimeout = useRef(null);
  const LIMIT = 30;

  const fetchCandidates = useCallback(async (pg = 1, q = "", sk = "", ws = "") => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (q)  params.set("search",      q);
      if (sk) params.set("skills",      sk);
      if (ws) params.set("work_status", ws);
      const res  = await authFetch(`/api/candidates/all?${params}`);
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data.error || "Failed to load candidates");
      setCandidates(data.data || []);
      setTotal(data.total || 0);
      setPage(data.page   || 1);
      setPages(data.pages || 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const handleSearchChange = val => {
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      fetchCandidates(1, val, skillFilter, workStatus);
    }, 420);
  };

  // S3358: helper to compute work-status badge styles
  const workStatusStyle = ws => {
    const isActive = ws.toLowerCase().includes("active");
    return {
      bg:    isActive ? "#f0fdf4" : "#f8fafc",
      color: isActive ? "#16a34a" : "#64748b",
    };
  };

  return (
    <div className="view-anim">
      <div className="page-hero">
        <div>
          <h1 className="page-h1">All Candidates</h1>
          <p className="page-sub">{total} registered candidates in the database</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn--outline btn--icon-only"
            onClick={() => setShowFilters(v => !v)}
            style={showFilters ? { background: "#eef2ff", color: "#4338ca", borderColor: "#6366f1" } : {}}
          >
            <span className="icon-wrap">{Icons.filter}</span>
          </button>
          <button className="btn btn--outline" onClick={() => fetchCandidates(page, search, skillFilter, workStatus)}>
            <span className="icon-wrap">{Icons.refresh}</span> <span className="hide-xs">Refresh</span>
          </button>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="recruiter-search-box" style={{ flex: 1 }}>
          <span className="icon-wrap">{Icons.search}</span>
          <input placeholder="Search by name, email, username…" value={searchInput} onChange={e => handleSearchChange(e.target.value)} />
          {searchInput && (
            <button className="clear-btn" onClick={() => { setSearchInput(""); setSearch(""); fetchCandidates(1, "", skillFilter, workStatus); }}>
              <span className="icon-wrap">{Icons.x}</span>
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="advanced-filters">
          <div className="af-row">
            <div className="af-group">
              <label htmlFor="af-skill">Filter by Skill</label>
              <input id="af-skill" type="text" placeholder="e.g. React, Python" value={skillFilter} onChange={e => setSkillFilter(e.target.value)} />
            </div>
            <div className="af-group">
              <label htmlFor="af-workstatus">Work Status</label>
              <select id="af-workstatus" value={workStatus} onChange={e => setWorkStatus(e.target.value)}>
                <option value="">Any</option>
                <option value="Actively looking">Actively Looking</option>
                <option value="Open to offers">Open to Offers</option>
                <option value="Not looking">Not Looking</option>
              </select>
            </div>
            <div className="af-actions">
              <button className="btn btn--primary btn--sm" onClick={() => { fetchCandidates(1, search, skillFilter, workStatus); setShowFilters(false); }}>Apply</button>
              <button className="btn btn--ghost btn--sm"   onClick={() => { setSkillFilter(""); setWorkStatus(""); fetchCandidates(1, search, "", ""); setShowFilters(false); }}>Clear</button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="empty-state">
          <span style={{ fontSize: "2rem" }}>⚠️</span>
          <p>{error}</p>
          <button className="btn btn--outline" onClick={() => fetchCandidates(page, search, skillFilter, workStatus)}>
            <span className="icon-wrap">{Icons.refresh}</span> Retry
          </button>
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 12, color: "#94a3b8" }}>
          <div className="spinner" />Loading candidates…
        </div>
      )}

      {!loading && !error && candidates.length === 0 && (
        <div className="empty-state">
          <span style={{ fontSize: "2.8rem" }}>👥</span>
          <p style={{ fontWeight: 700 }}>{total === 0 ? "No candidates registered yet" : "No candidates match your filters"}</p>
        </div>
      )}

      {!loading && !error && candidates.length > 0 && (
        <>
          <div className="table-wrap desktop-table">
            <table className="c-table">
              <thead><tr><th>Candidate</th><th>Skills</th><th>Work Status</th><th>Location</th><th>Apps</th><th>Tickets</th><th>Joined</th><th>Contact</th><th>Actions</th></tr></thead>
              <tbody>
                {candidates.map((c, i) => {
                  const name       = c.full_name || ((c.first_name || "") + " " + (c.last_name || "")).trim() || c.username || "Unknown";
                  const initials   = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const skills     = Array.isArray(c.skills) ? c.skills : [];
                  const locs       = Array.isArray(c.locations) ? c.locations : [];
                  const joinedDays = c.created_at ? Math.floor((Date.now() - new Date(c.created_at)) / 86400000) : null;
                  let joinedStr;
                  if (joinedDays === null) { joinedStr = "—"; }
                  else if (joinedDays === 0) { joinedStr = "Today"; }
                  else if (joinedDays <= 30) { joinedStr = joinedDays + "d ago"; }
                  else { joinedStr = new Date(c.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" }); }

                  const appCount = Number(c.application_count);
                  const cmpCount = Number(c.complaint_count);
                  const wsStyle  = c.work_status ? workStatusStyle(c.work_status) : null;

                  return (
                    <tr key={c.id} className="c-row" onClick={() => setSelected(c)}>
                      <td><div className="c-cell"><Avatar initials={initials} size={34} idx={i} photo={c.profile_photo || null} /><div><p className="c-name">{name}</p><p className="c-email">{c.email || "—"}</p></div></div></td>
                      <td>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, maxWidth: 200 }}>
                          {skills.slice(0, 3).map(s => <span key={s} className="skill-tag sm">{s}</span>)}
                          {skills.length > 3 && <span className="skill-tag sm" style={{ background: "#f1f5f9", color: "#64748b" }}>+{skills.length - 3}</span>}
                          {!skills.length && <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>}
                        </div>
                      </td>
                      <td>
                        {c.work_status && wsStyle
                          ? <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: wsStyle.bg, color: wsStyle.color }}>{c.work_status}</span>
                          : <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>
                        }
                      </td>
                      <td style={{ fontSize: 12.5, color: "#374151" }}>{locs.slice(0, 2).join(", ") || "—"}</td>
                      <td><span style={{ fontSize: 13, fontWeight: 800, padding: "3px 9px", borderRadius: 99, background: appCount > 0 ? "#eef2ff" : "#f8fafc", color: appCount > 0 ? "#4338ca" : "#94a3b8" }}>{c.application_count || 0}</span></td>
                      <td><span style={{ fontSize: 13, fontWeight: 800, padding: "3px 9px", borderRadius: 99, background: cmpCount > 0 ? "#fef2f2" : "#f8fafc", color: cmpCount > 0 ? "#dc2626" : "#94a3b8" }}>{c.complaint_count || 0}</span></td>
                      <td className="date-td">{joinedStr}</td>
                      <td onClick={e => e.stopPropagation()}><ContactButtons email={c.email || ""} phone={c.mobile || ""} name={name.split(" ")[0]} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="c-actions">
                          <button className="icon-btn sm" title="View profile" onClick={() => setSelected(c)}><span className="icon-wrap">{Icons.eye}</span></button>
                          {c.username && (
                            <a className="icon-btn sm" title="Open public profile" href={getPublicProfileUrl(c.username)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ textDecoration: "none" }}>
                              <span className="icon-wrap">{Icons.externalLink}</span>
                            </a>
                          )}
                          <button
                            className="icon-btn sm"
                            title="Raise ticket against this candidate"
                            style={{ color: "#263012" }}
                            onClick={e => { e.stopPropagation(); onRaiseTicket?.({ id: c.id, full_name: name, candidate_name: name }); }}
                          >
                            <span className="icon-wrap">{Icons.ticket}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mobile-cards">
            {candidates.map((c, i) => {
              const name     = c.full_name || ((c.first_name || "") + " " + (c.last_name || "")).trim() || c.username || "Unknown";
              const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
              const skills   = Array.isArray(c.skills) ? c.skills : [];
              return (
                <div key={c.id} className="mobile-card" onClick={() => setSelected(c)}>
                  <div className="mc-header">
                    <Avatar initials={initials} size={40} idx={i} photo={c.profile_photo || null} />
                    <div className="mc-info"><p className="mc-name">{name}</p><p className="mc-sub">{c.headline || c.email || "—"}</p></div>
                    {c.work_status && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: "#eef2ff", color: "#4338ca", whiteSpace: "nowrap" }}>{c.work_status}</span>}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <span className="skill-tag sm" style={{ background: "#eef2ff", color: "#4338ca" }}>{c.application_count || 0} apps</span>
                    <span className="skill-tag sm" style={{ background: "#fef2f2", color: "#dc2626" }}>{c.complaint_count || 0} tickets</span>
                  </div>
                  {skills.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                      {skills.slice(0, 4).map(s => <span key={s} className="skill-tag sm">{s}</span>)}
                      {skills.length > 4 && <span className="skill-tag sm" style={{ background: "#f1f5f9", color: "#64748b" }}>+{skills.length - 4}</span>}
                    </div>
                  )}
                  <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
                    <ContactButtons email={c.email || ""} phone={c.mobile || ""} name={name.split(" ")[0]} />
                  </div>
                  <div className="mc-actions" onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
                    <button
                      className="mc-btn"
                      style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" }}
                      onClick={() => onRaiseTicket?.({ id: c.id, full_name: name, candidate_name: name })}
                    >
                      🎫 Raise Ticket
                    </button>
                    {c.username && (
                      <a href={getPublicProfileUrl(c.username)} target="_blank" rel="noreferrer" className="mc-btn" style={{ background: "#eef2ff", color: "#4338ca", textDecoration: "none" }} onClick={e => e.stopPropagation()}>
                        View Profile
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {pages > 1 && (
            <div className="pagination">
              <button className="page-btn" disabled={page <= 1}    onClick={() => fetchCandidates(page - 1, search, skillFilter, workStatus)}>← Prev</button>
              <div className="page-nums">
                {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                  let pg = i + 1;
                  if (pages > 5) {
                    if (page <= 3)          pg = i + 1;
                    else if (page >= pages - 2) pg = pages - 4 + i;
                    else                        pg = page - 2 + i;
                  }
                  return (
                    <button key={pg} className={`page-num ${page === pg ? "page-num--on" : ""}`} onClick={() => fetchCandidates(pg, search, skillFilter, workStatus)}>
                      {pg}
                    </button>
                  );
                })}
              </div>
              <button className="page-btn" disabled={page >= pages} onClick={() => fetchCandidates(page + 1, search, skillFilter, workStatus)}>Next →</button>
              <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 6 }}>{total} total</span>
            </div>
          )}
        </>
      )}

      {selected && (
        <CandidateDrawer
          app={selected}
          jobSkills=""
          isCandidate
          onClose={() => setSelected(null)}
          onRaiseTicket={onRaiseTicket}
        />
      )}
    </div>
  );
}
CandidatesView.propTypes = {
  onNavTo:      PropTypes.func.isRequired,
  onRaiseTicket: PropTypes.func.isRequired,
};

// ══════════════════════════════════════════════════════════════════════
// ComplaintsView
// ══════════════════════════════════════════════════════════════════════
function ComplaintsView({ onNavTo, onRaiseTicket }) {
  const [complaints, setComplaints] = useState([]);
  const [summary,    setSummary]    = useState({ total: 0, open: 0, resolved: 0, hidden: 0 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [search,     setSearch]     = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res  = await authFetch("/api/recruiter/complaints");
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data?.error || "Failed to load tickets");
      setComplaints(Array.isArray(data.complaints) ? data.complaints : []);
      setSummary(data.summary || { total: 0, open: 0, resolved: 0, hidden: 0 });
    } catch (err) {
      setError(err.message || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeComplaint = async item => {
    if (!item?.id) return;
    // S7764: use globalThis
    if (!window.confirm("Delete this ticket?")) return;
    try {
      const res  = await authFetch(`/api/recruiter/complaints/${item.id}`, { method: "DELETE" });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data?.error || "Failed to delete ticket");
      setComplaints(prev => prev.filter(r => r.id !== item.id));
    } catch (err) {
      setError(err.message || "Failed to delete ticket");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return complaints;
    const q = search.toLowerCase();
    return complaints.filter(c =>
      (c.candidate_name     || "").toLowerCase().includes(q) ||
      (c.candidate_username || "").toLowerCase().includes(q) ||
      (c.title              || "").toLowerCase().includes(q) ||
      (c.category           || "").toLowerCase().includes(q)
    );
  }, [complaints, search]);

  if (loading) return (
    <div className="view-anim" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 14, color: "#94a3b8" }}>
      <div className="spinner" />Loading tickets…
    </div>
  );

  return (
    <div className="view-anim">
      <div className="page-hero">
        <div>
          <h1 className="page-h1">Complaint Tickets</h1>
          <p className="page-sub">{summary.total} total · {summary.open} open · {summary.resolved} resolved</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn--outline" onClick={load}><span className="icon-wrap">{Icons.refresh}</span> Refresh</button>
          <button className="btn btn--primary" onClick={() => onNavTo("candidates")} title="Go to All Candidates and select a candidate to raise a ticket">
            <span className="icon-wrap">{Icons.ticket}</span> Raise Ticket
          </button>
        </div>
      </div>

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Total Tickets"   value={summary.total    || 0} trend="All time"         icon="bell"  />
        <StatCard label="Open"            value={summary.open     || 0} trend="Needs attention"  icon="clock" accent />
        <StatCard label="Resolved"        value={summary.resolved || 0} trend="Closed tickets"   icon="check" />
        <StatCard label="Hidden by Admin" value={summary.hidden   || 0} trend="Hidden from view" icon="eye"  />
      </div>

      {error && <div className="post-job-error" style={{ marginBottom: 16 }}>⚠️ {error}</div>}

      {complaints.length === 0 ? (
        <div className="empty-state">
          <span style={{ fontSize: "2.8rem" }}>🛡️</span>
          <p style={{ fontWeight: 700 }}>No complaint tickets yet</p>
          <p style={{ fontSize: ".82rem", color: "#94a3b8", marginTop: 4, maxWidth: 340 }}>
            To raise a ticket, go to <strong>All Candidates</strong>, find the candidate, and click the 🎫 <strong>Raise Ticket</strong> button next to their name.
          </p>
          <button className="btn btn--primary" style={{ marginTop: 18 }} onClick={() => onNavTo("candidates")}>
            Go to All Candidates →
          </button>
        </div>
      ) : (
        <>
          <div className="toolbar" style={{ marginBottom: 14 }}>
            <div className="recruiter-search-box" style={{ flex: 1, maxWidth: 400 }}>
              <span className="icon-wrap">{Icons.search}</span>
              <input placeholder="Search by candidate, title, category…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button className="clear-btn" onClick={() => setSearch("")}><span className="icon-wrap">{Icons.x}</span></button>}
            </div>
            <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: "auto", alignSelf: "center" }}>{filtered.length} tickets</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: "#fef9ec", border: "1px solid #fde68a", marginBottom: 18, fontSize: 13, color: "#92400e" }}>
            <span>💡</span>
            <span>To raise a new ticket, go to <strong>All Candidates</strong> and click the 🎫 icon next to any candidate.</span>
            <button className="btn btn--sm" style={{ marginLeft: "auto", background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", fontWeight: 700 }} onClick={() => onNavTo("candidates")}>
              Go to Candidates →
            </button>
          </div>

          <div className="table-wrap desktop-table">
            <table className="c-table">
              <thead>
                <tr><th>Candidate</th><th>Title</th><th>Category</th><th>Status</th><th>Created</th><th>Proof</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  // S7781: replaceAll
                  const categoryLabel = String(item.category || "").replaceAll("_", " ");
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="c-cell">
                          <Avatar initials={(item.candidate_name || "C").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()} size={34} idx={0} photo={item.candidate_photo || null} />
                          <div>
                            <p className="c-name">{item.candidate_name || "Candidate"}</p>
                            <p className="c-email">{item.candidate_username ? "@" + item.candidate_username : item.candidate_email || "—"}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</td>
                      <td style={{ textTransform: "capitalize", fontSize: 13 }}>{categoryLabel}</td>
                      <td><StatusChip status={item.status} /></td>
                      <td className="date-td">{fmtDate(item.created_at)}</td>
                      <td>
                        {item.proof_url
                          ? <a href={resolveApiUploadUrl(item.proof_url)} target="_blank" rel="noreferrer" className="btn btn--outline btn--sm">Open proof</a>
                          : <span style={{ color: "#94a3b8", fontSize: 12 }}>None</span>
                        }
                      </td>
                      <td>
                        <div className="c-actions">
                          {item.candidate_profile_url && (
                            <a className="icon-btn sm" title="Open profile" href={item.candidate_profile_url} target="_blank" rel="noreferrer">
                              <span className="icon-wrap">{Icons.externalLink}</span>
                            </a>
                          )}
                          {item.can_delete && (
                            <button className="icon-btn sm danger" title="Delete ticket" onClick={() => removeComplaint(item)}>
                              <span className="icon-wrap">{Icons.trash}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mobile-cards">
            {filtered.map(item => {
              // S7781: replaceAll
              const categoryLabel = String(item.category || "").replaceAll("_", " ");
              return (
                <div key={item.id} className="mobile-card">
                  <div className="mc-header">
                    <Avatar initials={(item.candidate_name || "C").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()} size={40} idx={0} photo={item.candidate_photo || null} />
                    <div className="mc-info">
                      <p className="mc-name">{item.candidate_name || "Candidate"}</p>
                      <p className="mc-sub">{item.title}</p>
                    </div>
                    <StatusChip status={item.status} />
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                    <span style={{ textTransform: "capitalize" }}>{categoryLabel}</span>
                    <span style={{ margin: "0 6px" }}>·</span>
                    <span>{fmtDate(item.created_at)}</span>
                  </div>
                  <div className="mc-actions" style={{ marginTop: 8 }}>
                    {item.proof_url && <a href={resolveApiUploadUrl(item.proof_url)} target="_blank" rel="noreferrer" className="mc-btn" style={{ background: "#eef2ff", color: "#4338ca", textDecoration: "none" }}>Proof</a>}
                    {item.can_delete && <button className="mc-btn mc-btn--red" onClick={() => removeComplaint(item)}>Delete</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
ComplaintsView.propTypes = {
  onNavTo:      PropTypes.func.isRequired,
  onRaiseTicket: PropTypes.func.isRequired,
};

// ─── DashboardView ───────────────────────────────────────────────────
function DashboardView({ appCounts, onNavTo, postedJobs, recentApps }) {
  const stageCount = {};
  STAGES.forEach(s => {
    stageCount[s] = recentApps.filter(a => {
      if (s === "Applied")   return !a.status || a.status === "pending" || a.status === "applied";
      if (s === "Screening") return a.status === "shortlisted";
      if (s === "Interview") return a.status === "interview";
      if (s === "Hired")     return a.status === "hired";
      if (s === "Rejected")  return a.status === "rejected";
      return false;
    }).length;
  });
  const maxStage = Math.max(...Object.values(stageCount), 1);

  // S3358: extract score color helper
  const scoreColor = score => {
    if (score >= 85) return "#263012";
    if (score >= 70) return "#4338ca";
    return "#394420";
  };

  return (
    <div className="view-anim">
      <div className="page-hero"><div><h1 className="page-h1">Good Morning, Recruiter 👋</h1><p className="page-sub">Here&apos;s your hiring snapshot for today</p></div></div>
      <div className="stat-grid">
        <StatCard label="Total Applications" value={appCounts.total     || 0} trend="From real candidates"  icon="people"   />
        <StatCard label="Pending Review"      value={appCounts.pending   || 0} trend="Needs your attention" icon="inbox"    accent />
        <StatCard label="In Interview"        value={appCounts.interview || 0} trend="Scheduled interviews" icon="calendar" />
        <StatCard label="Hired"               value={appCounts.hired     || 0} trend="Successful hires"     icon="check"    />
      </div>
      {(appCounts.pending || 0) > 0 && (
        /* S6848 / S1082: use button with role and keyboard handler */
        <div
          className="app-alert"
          role="button"
          tabIndex={0}
          onClick={() => onNavTo("applications")}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onNavTo("applications"); }}
        >
          <span className="app-alert__icon">📬</span>
          <div><strong>{appCounts.pending} new application{appCounts.pending !== 1 ? "s" : ""}</strong> waiting for your review.</div>
          <button className="btn btn--primary btn--sm">Review Now →</button>
        </div>
      )}
      <div className="dash-grid">
        <div className="card">
          <div className="card__header"><h3>Recruitment Pipeline</h3></div>
          <div className="pipeline-bars">
            {STAGES.map(s => (
              <div className="pipeline-row" key={s}>
                <span className="pl-label">{s}</span>
                <div className="pl-track"><div className="pl-fill" style={{ width: `${(stageCount[s] / maxStage) * 100}%` }} /></div>
                <span className="pl-num">{stageCount[s]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card__header"><h3>Latest Applications</h3><button className="see-all-btn" onClick={() => onNavTo("applications")}>See all</button></div>
          <div className="new-apps-list">
            {recentApps.length === 0 && <div style={{ padding: "14px 0", color: "#94a3b8", fontSize: 13, textAlign: "center" }}>No applications yet.</div>}
            {recentApps.slice(0, 5).map((app, i) => {
              const name     = (app.candidate_name || app.user_name || "?").trim();
              const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
              const score    = app.matchScore || 0;
              return (
                <div className="new-app-item" key={app.id}>
                  <Avatar initials={initials} size={34} idx={i} photo={app.candidate_photo || null} />
                  <div className="na-info"><p className="na-name">{name}</p><p className="na-role">{app.job_title || "—"}</p></div>
                  <span style={{ fontSize: 11, fontWeight: 800, color: scoreColor(score) }}>{score}%</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card">
          <div className="card__header"><h3>Open Positions</h3><button className="see-all-btn" onClick={() => onNavTo("jobs")}>See all</button></div>
          <div className="open-jobs-list">
            {postedJobs.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13, padding: "12px 0" }}>No jobs posted yet.</div>}
            {postedJobs.filter(j => j.status === "Active").slice(0, 5).map(j => (
              <div className="oj-item" key={j.id}>
                <div className="oj-dot" style={{ background: "#263012" }} />
                <div className="oj-info"><p className="oj-title">{j.title}</p><p className="oj-dept">{j.dept}</p></div>
                <span className="oj-count">{j.applicants || 0}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card__header"><h3>Quick Stats</h3></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 11, padding: "4px 0" }}>
            {[
              { label: "Shortlisted", val: appCounts.shortlisted || 0, color: "#4338ca" },
              { label: "In Interview", val: appCounts.interview || 0, color: "#0891b2" },
              { label: "Hired",        val: appCounts.hired || 0,      color: "#263012" },
              { label: "Rejected",     val: appCounts.rejected || 0,   color: "#394420" },
            ].map(item => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: "#374151" }}>{item.label}</span>
                <div style={{ flex: 2, height: 5, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (item.val / Math.max(appCounts.total || 1, 1)) * 100)}%`, background: item.color, borderRadius: 99, transition: "width 1s ease" }} />
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: item.color, minWidth: 18, textAlign: "right" }}>{item.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
DashboardView.propTypes = {
  appCounts:  PropTypes.object.isRequired,
  onNavTo:    PropTypes.func.isRequired,
  postedJobs: PropTypes.array.isRequired,
  recentApps: PropTypes.array.isRequired,
};

// ─── JobsView ────────────────────────────────────────────────────────
function JobsView({ postedJobs, onJobPosted, employerId, companyName, onRefresh }) {
  const [jobs, setJobs]         = useState(postedJobs);
  // S6754: destructure useState properly
  const [showModal, setModal]   = useState(false);
  const [toggling, setToggling] = useState(null);
  useEffect(() => setJobs(postedJobs), [postedJobs]);

  const toggleStatus = async id => {
    const job    = jobs.find(j => j.id === id);
    const newStat = job.status === "Active" ? "paused" : "active";
    setToggling(id);
    try { await authFetch(`/employer/jobs/${id}`, { method: "PATCH", body: JSON.stringify({ status: newStat }) }); } catch { /* ignore */ }
    setJobs(prev => prev.map(j => j.id === id ? { ...j, status: j.status === "Active" ? "Paused" : "Active" } : j));
    setToggling(null);
  };

  const deleteJob = async id => {
    // S7764: globalThis
    if (!window.confirm("Remove this job?")) return;
    try { await authFetch(`/employer/jobs/${id}`, { method: "PATCH", body: JSON.stringify({ status: "deleted" }) }); } catch { /* ignore */ }
    setJobs(prev => prev.filter(j => j.id !== id));
  };

  return (
    <div className="view-anim">
      <div className="page-hero">
        <div><h1 className="page-h1">Job Openings</h1><p className="page-sub">{jobs.filter(j => j.status === "Active").length} active · {jobs.length} total</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn--outline" onClick={onRefresh}><span className="icon-wrap">{Icons.refresh}</span> <span className="hide-xs">Refresh</span></button>
          <button className="btn btn--primary" onClick={() => setModal(true)}><span className="icon-wrap">{Icons.plus}</span> <span className="hide-xs">Post Job</span><span className="show-xs">New</span></button>
        </div>
      </div>
      <div className="jobs-link-banner"><span>🔗</span><span>Jobs posted here appear live for candidates to find and apply.</span><a href="/employee/jobs" target="_blank" rel="noreferrer" className="banner-link">View Live <span className="icon-wrap">{Icons.externalLink}</span></a></div>
      {jobs.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 40 }}>
          <span style={{ fontSize: "2.8rem" }}>📋</span>
          <p style={{ fontWeight: 700 }}>No jobs posted yet</p>
          <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={() => setModal(true)}><span className="icon-wrap">{Icons.plus}</span> Post Your First Job</button>
        </div>
      ) : (
        <div className="jobs-grid">
          {jobs.map(j => (
            <div className="job-card" key={j.id}>
              <div className="job-card__top"><div className="job-dept-badge">{j.dept}</div><span className={`job-status-badge ${j.status === "Active" ? "jsb-active" : "jsb-paused"}`}>{j.status === "Active" ? "🟢 Live" : "⏸ Paused"}</span></div>
              <h3 className="job-card__title">{j.title}</h3><p className="job-card__type">{j.type}</p>
              <div className="job-card__stats"><div className="jcs-item"><span className="jcs-val">{j.applicants || 0}</span><span className="jcs-label">Applicants</span></div><div className="jcs-divider" /><div className="jcs-item"><span className="jcs-val">{j.deadline || "—"}</span><span className="jcs-label">Deadline</span></div></div>
              <div className="job-card__footer">
                <button className="btn btn--outline btn--sm" onClick={() => window.open("/employee/jobs", "_blank")}>View Live</button>
                <button className="btn btn--ghost btn--sm" disabled={toggling === j.id} onClick={() => toggleStatus(j.id)}>{toggling === j.id ? "…" : j.status === "Active" ? "Pause" : "Activate"}</button>
                <button className="icon-btn sm danger" onClick={() => deleteJob(j.id)}><span className="icon-wrap">{Icons.trash}</span></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && <PostJobModal onClose={() => setModal(false)} onJobPosted={j => { onJobPosted(j); setModal(false); }} employerId={employerId} companyName={companyName} />}
    </div>
  );
}
JobsView.propTypes = {
  postedJobs:  PropTypes.array.isRequired,
  onJobPosted: PropTypes.func.isRequired,
  employerId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyName: PropTypes.string,
  onRefresh:   PropTypes.func.isRequired,
};
JobsView.defaultProps = { employerId: null, companyName: "" };

// ─── PipelineView ────────────────────────────────────────────────────
function PipelineView() {
  const [applications, setApplications] = useState([]);
  const [jobsMap,      setJobsMap]      = useState({});
  const [loading,      setLoading]      = useState(true);
  const [selectedApp,  setSelectedApp]  = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [appRes, jobRes] = await Promise.all([authFetch("/api/applications/recruiter"), authFetch("/api/employer/profile")]);
        const [appData, jobData] = await Promise.all([readJsonResponse(appRes), readJsonResponse(jobRes)]);
        const jobs = Array.isArray(jobData) ? jobData : (jobData.data || []);
        const jm = {};
        jobs.forEach(j => { jm[j.id] = j.skills || ""; });
        setJobsMap(jm);
        const list = (Array.isArray(appData) ? appData : (appData.data || [])).map(app => {
          // S3358: extract stageKey logic
          let stageKey = "Applied";
          if      (!app.status || app.status === "pending" || app.status === "applied") stageKey = "Applied";
          else if (app.status === "shortlisted") stageKey = "Screening";
          else if (app.status === "interview")   stageKey = "Interview";
          else if (app.status === "hired")       stageKey = "Hired";
          else if (app.status === "rejected")    stageKey = "Rejected";
          return { ...app, matchScore: calcMatchScore(app.user_skills || "", jm[app.job_id] || ""), stageKey };
        });
        setApplications(list);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const stageMap = STAGES.reduce((a, s) => ({ ...a, [s]: applications.filter(app => app.stageKey === s) }), {});
  const stageColors = { "Applied": "#4338ca", "Screening": "#7c3aed", "Interview": "#394420", "Offer Sent": "#6366f1", "Hired": "#263012", "Rejected": "#394420" };

  if (loading) return (
    <div className="view-anim" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 12, color: "#94a3b8" }}>
      <div className="spinner" />Loading pipeline…
    </div>
  );

  return (
    <div className="view-anim">
      <div className="page-hero"><div><h1 className="page-h1">Pipeline Board</h1><p className="page-sub">Live hiring kanban · {applications.length} total</p></div></div>
      {applications.length === 0 ? (
        <div className="empty-state"><span style={{ fontSize: "2.8rem" }}>📋</span><p>No candidates in pipeline yet.</p></div>
      ) : (
        <div className="kanban">
          {STAGES.map(s => (
            <div className="kanban-col" key={s}>
              <div className="kanban-col__header" style={{ borderTopColor: stageColors[s] }}>
                <span className="kch-label">{s}</span>
                <span className="kch-count" style={{ background: stageColors[s] + "22", color: stageColors[s] }}>{stageMap[s].length}</span>
              </div>
              <div className="kanban-cards">
                {stageMap[s].map((app, i) => {
                  const name     = (app.candidate_name || "?").trim();
                  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const score    = app.matchScore || 0;
                  const sk       = app.user_skills || "";
                  let arr;
                  if (Array.isArray(sk)) { arr = sk; }
                  else if (sk.startsWith("[")) {
                    try { arr = JSON.parse(sk); } catch { arr = sk.split(",").map(x => x.trim()).filter(Boolean); }
                  } else {
                    arr = sk.split(",").map(x => x.trim()).filter(Boolean);
                  }
                  return (
                    <div className="k-card" key={app.id} onClick={() => setSelectedApp(app)}>
                      <div className="k-card__top"><Avatar initials={initials} size={30} idx={i} photo={app.candidate_photo || null} /><ScoreRing score={score} size={36} /></div>
                      <p className="k-name">{name}</p><p className="k-role">{app.job_title || "—"}</p>
                      <div className="k-skills">{arr.slice(0, 2).map(s2 => <span className="skill-tag sm" key={s2}>{s2}</span>)}</div>
                    </div>
                  );
                })}
                {stageMap[s].length === 0 && <div className="k-empty">No candidates</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedApp && (
        <CandidateDrawer
          app={selectedApp}
          jobSkills={jobsMap[selectedApp.job_id] || ""}
          onClose={() => setSelectedApp(null)}
          onDecision={(id, d) => {
            setApplications(prev => prev.map(a => {
              if (a.id !== id) return a;
              let stageKey = "Applied";
              if      (!d || d === "pending" || d === "applied") stageKey = "Applied";
              else if (d === "shortlisted") stageKey = "Screening";
              else if (d === "interview")   stageKey = "Interview";
              else if (d === "hired")       stageKey = "Hired";
              else if (d === "rejected")    stageKey = "Rejected";
              return { ...a, status: d, stageKey };
            }));
            setSelectedApp(null);
          }}
          onRaiseTicket={() => {}}
        />
      )}
    </div>
  );
}

// ─── ReportsView ─────────────────────────────────────────────────────
function ReportsView({ appCounts, postedJobs }) {
  const total = Math.max(appCounts.total || 1, 1);
  const maxApplicants = Math.max(...postedJobs.map(x => x.applicants || 0), 1);
  return (
    <div className="view-anim">
      <div className="page-hero"><div><h1 className="page-h1">Reports &amp; Analytics</h1><p className="page-sub">Live data from your hiring pipeline</p></div></div>
      <div className="stat-grid">
        <StatCard label="Total Applications" value={appCounts.total || 0} trend="All time"              icon="people"    />
        <StatCard label="Shortlisted"         value={appCounts.shortlisted || 0} trend="Ready to interview" icon="star"  />
        <StatCard label="Hired"               value={appCounts.hired || 0}  trend="Successful hires"    icon="check"     accent />
        <StatCard label="Active Jobs"         value={postedJobs.filter(j => j.status === "Active").length} trend="Live on portal" icon="briefcase" />
      </div>
      <div className="reports-grid">
        <div className="card">
          <div className="card__header"><h3>Application Status Breakdown</h3></div>
          <div className="breakdown-list">
            {[
              { k: "pending",     label: "Pending",     val: appCounts.pending     || 0 },
              { k: "shortlisted", label: "Shortlisted", val: appCounts.shortlisted || 0 },
              { k: "interview",   label: "Interview",   val: appCounts.interview   || 0 },
              { k: "hired",       label: "Hired",       val: appCounts.hired       || 0 },
              { k: "rejected",    label: "Rejected",    val: appCounts.rejected    || 0 },
            ].map(({ k, label, val }) => {
              const cfg = APP_STATUS_CONFIG[k] || { color: "#94a3b8" };
              return (
                <div className="bd-row" key={k}>
                  <span className="bd-dot" style={{ background: cfg.color }} />
                  <span className="bd-label">{label}</span>
                  <div className="bd-track"><div className="bd-fill" style={{ width: `${(val / total) * 100}%`, background: cfg.color }} /></div>
                  <span className="bd-count">{val}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card">
          <div className="card__header"><h3>Jobs Performance</h3></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {postedJobs.slice(0, 8).map(j => (
              <div key={j.id} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.title}</span>
                <div style={{ flex: 2, height: 5, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, ((j.applicants || 0) / maxApplicants) * 100)}%`, background: "var(--brand)", borderRadius: 99 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--brand)", minWidth: 28, textAlign: "right" }}>{j.applicants || 0}</span>
              </div>
            ))}
            {postedJobs.length === 0 && <p style={{ color: "#94a3b8", fontSize: 13 }}>Post jobs to see metrics</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
ReportsView.propTypes = {
  appCounts:  PropTypes.object.isRequired,
  postedJobs: PropTypes.array.isRequired,
};

// ─── ReviewsPage ─────────────────────────────────────────────────────
function ReviewsPage({ reviews, summary, notifications, onRequestRemoval, onMarkNotificationRead }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page,   setPage]   = useState(1);
  const PER = 12;

  const q    = search.toLowerCase();
  const list = reviews.filter(r => {
    const match = !q ||
      (r.user_name    || "").toLowerCase().includes(q) ||
      (r.user_email   || "").toLowerCase().includes(q) ||
      (r.comment      || "").toLowerCase().includes(q) ||
      (r.company_name || "").toLowerCase().includes(q) ||
      (r.tag          || "").toLowerCase().includes(q);
    const status = String(r.status || "visible");
    const ok = filter === "all" || status === filter;
    return match && ok;
  });
  const pages = Math.max(1, Math.ceil(list.length / PER));
  const paged = list.slice((page - 1) * PER, page * PER);

  return (
    <div className="sf">
      <div className="stat-row">
        <StatCard label="Total Reviews"     value={summary?.total             || reviews.length || 0} trend="All companies"     icon="star"  />
        <StatCard label="Pending"           value={summary?.pending           || 0}                   trend="Needs approval"    icon="clock" />
        <StatCard label="Visible"           value={summary?.visible           || 0}                   trend="Live now"          icon="check" accent />
        <StatCard label="Removal Requests"  value={summary?.removal_requested || 0}                   trend="Needs review"      icon="x"     />
      </div>
      {notifications?.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div style={{ padding: "16px 20px", borderBottom: "1.5px solid var(--border-light)", fontWeight: 800, fontSize: 14 }}>Review notifications</div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {notifications.slice(0, 5).map(n => (
              <div key={n.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 20px", borderBottom: "1px solid var(--border-light)" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: n.is_read ? "#cbd5e1" : "#263012", marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{n.message}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 3 }}>{n.event_type} · {fmtDate(n.created_at)}</div>
                </div>
                {!n.is_read && <button className="btn bo" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onMarkNotificationRead?.(n.id)}>Mark read</button>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search by reviewer, company, tag, comment..." />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[["all","All"],["visible","Visible"],["pending","Pending"],["removal_requested","Removal requested"],["hidden","Hidden"]].map(([k, l]) => (
            <button key={k} className={`tab ${filter === k ? "on" : ""}`} onClick={() => { setFilter(k); setPage(1); }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} reviews</span>
      </div>
      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "2fr 1.2fr 1fr 2.8fr 1fr 110px" }}>
          <span>Reviewer</span><span>Company</span><span>Rating</span><span>Review</span><span>Status</span><span></span>
        </div>
        {paged.map(r => (
          <div key={r.id} className="tr" style={{ gridTemplateColumns: "2fr 1.2fr 1fr 2.8fr 1fr 110px", alignItems: "start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.user_name || "Anonymous"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{r.user_email || "—"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{[r.position, r.city].filter(Boolean).join(" · ")}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.company_name || "—"}</div>
              {r.tag && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{r.tag}</div>}
            </div>
            <div style={{ fontWeight: 800, color: "#263012", fontSize: 16 }}>{r.rating || 0}/5</div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.5 }}>
              {(r.comment || r.review || "—").slice(0, 160)}{(r.comment || r.review || "").length > 160 ? "…" : ""}
              {r.removal_requested_reason && <div style={{ marginTop: 6, fontSize: 11.5, color: "#b91c1c" }}>Removal request: {r.removal_requested_reason}</div>}
            </div>
            <StatusChip status={r.status} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                className="btn bo"
                style={{ padding: "6px 10px", fontSize: 12 }}
                onClick={() => onRequestRemoval?.(r)}
                disabled={String(r.status || "") === "removal_requested"}
              >
                Request removal
              </button>
            </div>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No reviews found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}
ReviewsPage.propTypes = {
  reviews:                PropTypes.array.isRequired,
  summary:                PropTypes.object,
  notifications:          PropTypes.array,
  onRequestRemoval:       PropTypes.func,
  onMarkNotificationRead: PropTypes.func,
};
ReviewsPage.defaultProps = { summary: null, notifications: [], onRequestRemoval: null, onMarkNotificationRead: null };

// ─── RecommendationsPage ─────────────────────────────────────────────
function RecommendationsPage({ recommendations, summary, onEdit, onDelete }) {
  const [search, setSearch] = useState("");
  const [page,   setPage]   = useState(1);
  const PER = 12;
  const q    = search.toLowerCase();
  const list = recommendations.filter(r =>
    !q ||
    (r.candidate_name     || "").toLowerCase().includes(q) ||
    (r.candidate_username || "").toLowerCase().includes(q) ||
    (r.comment            || "").toLowerCase().includes(q) ||
    (r.reviewer_name      || "").toLowerCase().includes(q)
  );
  const pages = Math.max(1, Math.ceil(list.length / PER));
  const paged = list.slice((page - 1) * PER, page * PER);
  return (
    <div className="sf">
      <div className="stat-row">
        <StatCard label="My Recommendations" value={summary?.total || recommendations.length || 0} trend="Authored by you"  icon="star" accent />
        <StatCard label="Candidates"          value={new Set(recommendations.map(r => r.candidate_id)).size || 0} trend="Profiles touched" icon="people" />
        <StatCard label="Pending"             value={summary?.pending  || 0} trend="Awaiting approval" icon="clock" />
        <StatCard label="Approved"            value={summary?.approved || 0} trend="Live on profiles"  icon="check" />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <Search value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search by candidate or comment..." />
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{list.length} recommendations</span>
      </div>
      <div className="card">
        <div className="th" style={{ gridTemplateColumns: "1.6fr 1.2fr 2.5fr 0.8fr 0.8fr 110px" }}>
          <span>Candidate</span><span>Headline</span><span>Recommendation</span><span>Status</span><span>Date</span><span></span>
        </div>
        {paged.map(r => (
          <div key={r.id} className="tr" style={{ gridTemplateColumns: "1.6fr 1.2fr 2.5fr 0.8fr 0.8fr 110px", alignItems: "start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.candidate_name || "Candidate"}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>@{r.candidate_username || "unknown"}</div>
              {r.candidate_profile_url && <a href={r.candidate_profile_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--brand)", textDecoration: "none" }}>Open profile</a>}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.5 }}>{r.candidate_headline || "—"}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.5 }}>{(r.comment || "—").slice(0, 160)}{(r.comment || "").length > 160 ? "…" : ""}</div>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: r.status === "approved" ? "#065f46" : "#92400e", background: r.status === "approved" ? "rgba(16,185,129,0.14)" : "rgba(245,158,11,0.14)" }}>
                {r.status === "approved" ? "Approved" : "Pending"}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{fmtDate(r.created_at)}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button className="btn bo" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onEdit?.(r)}>Edit</button>
              <button className="btn bo" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => onDelete?.(r)}>Delete</button>
            </div>
          </div>
        ))}
        {!paged.length && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>No recommendations found</div>}
      </div>
      <PageButtons page={page} pages={pages} setPage={setPage} />
    </div>
  );
}
RecommendationsPage.propTypes = {
  recommendations: PropTypes.array.isRequired,
  summary:         PropTypes.object,
  onEdit:          PropTypes.func,
  onDelete:        PropTypes.func,
};
RecommendationsPage.defaultProps = { summary: null, onEdit: null, onDelete: null };

// ══════════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ══════════════════════════════════════════════════════════════════════
export default function RecruiterDashboard() {
  const [activeNav,             setActiveNav]             = useState("dashboard");
  const [postedJobs,            setPostedJobs]            = useState([]);
  const [appCounts,             setAppCounts]             = useState({ total: 0, pending: 0, shortlisted: 0, interview: 0, hired: 0, rejected: 0 });
  const [reviews,               setReviews]               = useState([]);
  const [reviewSummary,         setReviewSummary]         = useState({ total: 0, pending: 0, visible: 0, hidden: 0, removal_requested: 0 });
  const [reviewNotifications,   setReviewNotifications]   = useState([]);
  const [reviewUnread,          setReviewUnread]          = useState(0);
  const [recommendations,       setRecommendations]       = useState([]);
  const [recommendationSummary, setRecommendationSummary] = useState({ total: 0 });
  const [recentApps,            setRecentApps]            = useState([]);
  const [sidebarOpen,           setSidebarOpen]           = useState(false);
  const [employerProfile,       setEmployerProfile]       = useState(null);
  // Root-level complaint modal state
  const [complaintTarget, setComplaintTarget] = useState(null);

  const navigate = useNavigate();

  const currentUser  = readStoredUser();
  const employerId   = employerProfile?.id || currentUser?.id || null;
  const publicCoPath = employerId ? `/employee/company/${employerId}` : "/employer/profile";
  const companyName  = employerProfile?.company || currentUser?.company || (currentUser?.firstName ? ((currentUser.firstName || "") + " " + (currentUser.lastName || "")).trim() : "Our Company");

  useEffect(() => {
    let alive = true;
    authFetch("/api/employer/profile")
      .then(res => readJsonResponse(res))
      .then(data => { if (alive) setEmployerProfile(data || null); })
      .catch(() => { if (alive) setEmployerProfile(null); });
    return () => { alive = false; };
  }, []);

  const fetchMyJobs = useCallback(async () => {
    try {
      const res  = await authFetch("/api/employer/profile");
      const data = await readJsonResponse(res);
      if (!res.ok) return;
      const rows = Array.isArray(data) ? data : (data.data || data.jobs || []);
      const mine = employerId ? rows.filter(j => String(j.employer_id) === String(employerId)) : rows;
      setPostedJobs(mine.map(j => ({
        id:         j.id,
        title:      j.title,
        dept:       j.category || "General",
        applicants: Number(j.applicant_count || j.applications || 0),
        deadline:   j.expires_date
          ? new Date(j.expires_date).toLocaleDateString("en-IN", { month: "short", day: "numeric" })
          : "—",
        status:   j.status === "active" ? "Active" : "Paused",
        type:     j.type || "Full-time",
        location: j.location || "",
        skills:   j.skills || "",
      })));
    } catch (e) {
      console.warn("Could not fetch jobs:", e.message);
    }
  }, [employerId]);

  const fetchAppCounts = useCallback(async () => {
    try {
      const [appRes, jobRes] = await Promise.all([authFetch("/api/applications/recruiter"), authFetch("/api/employer/profile")]);
      const [data, jobData]  = await Promise.all([readJsonResponse(appRes), readJsonResponse(jobRes)]);
      const list = Array.isArray(data) ? data : (data.data || data.applications || []);
      const jobs = Array.isArray(jobData) ? jobData : (jobData.data || []);
      const jm = {};
      jobs.forEach(j => { jm[j.id] = j.skills || ""; });
      const enriched = list.map(app => ({
        ...app,
        matchScore: calcMatchScore(app.user_skills || app.candidate_skills || "", jm[app.job_id] || ""),
      }));
      setAppCounts({
        total:       list.length,
        pending:     list.filter(a => !a.status || a.status === "pending" || a.status === "applied").length,
        shortlisted: list.filter(a => a.status === "shortlisted").length,
        interview:   list.filter(a => a.status === "interview").length,
        hired:       list.filter(a => a.status === "hired").length,
        rejected:    list.filter(a => a.status === "rejected").length,
      });
      setRecentApps([...enriched].sort((a, b) => new Date(b.applied_at || 0) - new Date(a.applied_at || 0)).slice(0, 5));
    } catch (e) {
      console.warn("Could not fetch app counts:", e.message);
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      const [rr, nr] = await Promise.all([authFetch("/api/recruiter/reviews"), authFetch("/api/recruiter/review-notifications")]);
      const [rd, nd] = await Promise.all([readJsonResponse(rr), readJsonResponse(nr)]);
      const list = Array.isArray(rd?.reviews) ? rd.reviews : [];
      setReviews(list);
      setReviewSummary(rd?.summary || { total: list.length, pending: 0, visible: 0, hidden: 0, removal_requested: 0 });
      setReviewNotifications(Array.isArray(nd?.notifications) ? nd.notifications : []);
      setReviewUnread(Number(nd?.unread || 0));
    } catch (e) {
      console.warn("Reviews:", e.message);
    }
  }, []);

  const fetchRecommendations = useCallback(async () => {
    try {
      const res  = await authFetch("/api/recruiter/recommendations");
      const data = await readJsonResponse(res);
      const list = Array.isArray(data?.recommendations) ? data.recommendations : [];
      setRecommendations(list);
      setRecommendationSummary(data?.summary || { total: list.length });
    } catch (e) {
      console.warn("Recommendations:", e.message);
    }
  }, []);

  useEffect(() => {
    fetchMyJobs();
    fetchAppCounts();
    fetchReviews();
    fetchRecommendations();
  }, [fetchMyJobs, fetchAppCounts, fetchReviews, fetchRecommendations]);

  const handleJobPosted = useCallback(newJob => {
    setPostedJobs(prev => [{ ...newJob, status: "Active", applicants: 0 }, ...prev]);
  }, []);

  const handleRequestRemoval = useCallback(async review => {
    // S7764: globalThis
    const reason = window.prompt("Optional reason for the removal request:");
    if (reason === null) return;
    try {
      const res  = await authFetch(`/api/reviews/${review.id}/remove-request`, { method: "POST", body: JSON.stringify({ reason }) });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data?.error || "Failed");
      setReviews(prev => prev.map(r => r.id === review.id
        ? { ...r, status: "removal_requested", removal_requested_reason: reason || r.removal_requested_reason }
        : r
      ));
      fetchReviews();
      // S2681: wrap both statements in a block (single alert call — no multi-statement bug)
      window.alert("Removal request submitted.");
    } catch (e) {
      window.alert(e.message || "Failed");
    }
  }, [fetchReviews]);

  const handleEditRecommendation = useCallback(async rec => {
    const next = window.prompt("Edit your recommendation", rec.comment || "");
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed.length < 20) { window.alert("Recommendation must be at least 20 characters."); return; }
    try {
      const res  = await authFetch(`/api/candidate/recommendations/${rec.id}`, { method: "PATCH", body: JSON.stringify({ comment: trimmed }) });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data?.error || "Failed");
      setRecommendations(prev => prev.map(item => item.id === rec.id ? { ...item, comment: trimmed } : item));
      fetchRecommendations();
      window.alert("Recommendation updated.");
    } catch (e) {
      window.alert(e.message || "Failed");
    }
  }, [fetchRecommendations]);

  const handleDeleteRecommendation = useCallback(async rec => {
    if (window.confirm("Delete this recommendation?")) return;
    try {
      const res  = await authFetch(`/api/candidate/recommendations/${rec.id}`, { method: "DELETE" });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data?.error || "Failed");
      setRecommendations(prev => prev.filter(item => item.id !== rec.id));
      fetchRecommendations();
      window.alert("Recommendation deleted.");
    } catch (e) {
      window.alert(e.message || "Failed");
    }
  }, [fetchRecommendations]);

  const navTo = useCallback(id => {
    if (id === "profile") { navigate("/employer/profile"); setSidebarOpen(false); return; }
    setActiveNav(id); setSidebarOpen(false);
  }, [navigate]);

  const newCount    = (appCounts.pending || 0) + (reviewUnread || 0);
  const reviewBadge = (reviewSummary.pending || 0) + (reviewSummary.removal_requested || 0) + (reviewUnread || 0);

  const handleLogout = () => {
    ["th_employer_token","th_employer","th_token","th_user","token","user"].forEach(k => localStorage.removeItem(k));
    // S7764: globalThis
    window.location.href = "/";
  };

  const openRaiseTicket = useCallback(candidate => {
    setComplaintTarget(candidate);
  }, []);

  const closeComplaintModal = useCallback(() => {
    setComplaintTarget(null);
  }, []);

  // S7754: use .some() instead of .filter().length for existence check
  const activeJobsExist = postedJobs.some(j => j.status === "Active");
  const activeJobsCount = postedJobs.filter(j => j.status === "Active").length;

  return (
    <div className="app">
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          role="presentation"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={e => { if (e.key === "Escape") setSidebarOpen(false); }}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <div><div className="brand-name">TalentHive</div><div className="brand-tagline">Recruiter HQ</div></div>
          <button className="icon-btn sidebar-close hide-desktop" onClick={() => setSidebarOpen(false)}><span className="icon-wrap">{Icons.x}</span></button>
        </div>
        <nav className="sidebar__nav">
          {NAV_ITEMS.map(item => (
            <button key={item.id} className={`nav-btn ${activeNav === item.id ? "nav-btn--on" : ""}`} onClick={() => navTo(item.id)}>
              <span className="nav-icon icon-wrap">{Icons[item.icon] || Icons.grid}</span>
              <span className="nav-label">{item.label}</span>
              {item.id === "applications"    && newCount > 0          && <span className="nav-badge">{newCount}</span>}
              {item.id === "reviews"         && reviewBadge > 0       && <span className="nav-badge">{reviewBadge}</span>}
              {item.id === "recommendations" && recommendationSummary.total > 0 && <span className="nav-badge">{recommendationSummary.total}</span>}
              {item.id === "jobs"            && activeJobsExist       && <span className="nav-badge" style={{ background: "#263012" }}>{activeJobsCount}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar__bottom">
          <div className="user-block">
            <div className="user-ava">{(companyName || "R")[0].toUpperCase()}</div>
            <div className="user-meta"><span className="u-name">{companyName || "Recruiter"}</span><span className="u-role">HR Manager</span></div>
          </div>
          <button className="icon-btn dim" onClick={handleLogout} title="Sign out"><span className="icon-wrap">{Icons.logout}</span></button>
        </div>
      </aside>

      <main className="recruitermain">
        <header className="topbar">
          <div className="topbar__left">
            <button className="icon-btn hide-desktop topbar-menu-btn" onClick={() => setSidebarOpen(true)}><span className="icon-wrap">{Icons.menu}</span></button>
            <span className="topbar-nav-label">{NAV_ITEMS.find(n => n.id === activeNav)?.label}</span>
          </div>
          <div className="topbar__right">
            <div className="topbar-search hide-mobile">
              <span className="icon-wrap">{Icons.search}</span>
              {/* S6772: ensure clear spacing after icon element, use htmlFor */}
              <label htmlFor="topbar-search-input" style={{ display: "none" }}>Quick search</label>
              <input id="topbar-search-input" type="text" placeholder="Quick search…" />
            </div>
            <button className="icon-btn notif"><span className="icon-wrap">{Icons.bell}</span>{newCount > 0 && <span className="notif-pip" />}</button>
            <div className="topbar-user">{(companyName || "R")[0].toUpperCase()}</div>
          </div>
        </header>

        <div className="page-wrap">
          {activeNav === "dashboard"       && <DashboardView appCounts={appCounts} onNavTo={navTo} postedJobs={postedJobs} recentApps={recentApps} />}
          {activeNav === "profile"         && (
            <div className="view-anim">
              <div className="page-hero"><div><h1 className="page-h1">Company Profile</h1><p className="page-sub">Manage company information and public profile.</p></div></div>
              <div className="empty-state">
                <span style={{ fontSize: "3rem" }}>🏢</span>
                <p style={{ fontWeight: 700 }}>Edit Contact, Hiring, Jobs, Salaries, Interviews, Benefits, Photos, and Posts.</p>
                <div className="profile-quick-grid">
                  <div className="profile-quick-card"><strong>Contact</strong><span>Recruiter name, phone, email, website, LinkedIn</span></div>
                  <div className="profile-quick-card"><strong>Hiring</strong><span>Company type, size, address, urgency, open roles</span></div>
                  <div className="profile-quick-card"><strong>Public page</strong><span>Changes sync straight to the public company profile</span></div>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", marginTop: 18 }}>
                  <button className="btn btn--primary" onClick={() => navigate("/employer/profile")}>Open Company Profile →</button>
                  <button className="btn" onClick={() => navigate(publicCoPath)}>View Public Page</button>
                </div>
              </div>
            </div>
          )}
          {activeNav === "reviews" && (
            <ReviewsPage
              reviews={reviews}
              summary={reviewSummary}
              notifications={reviewNotifications}
              onRequestRemoval={handleRequestRemoval}
              onMarkNotificationRead={async id => {
                try {
                  const res = await authFetch(`/api/recruiter/review-notifications/${id}/read`, { method: "PATCH" });
                  if (!res.ok) return;
                  setReviewNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
                  setReviewUnread(prev => Math.max(0, prev - 1));
                } catch (e) {
                  console.warn(e);
                }
              }}
            />
          )}
          {activeNav === "recommendations" && <RecommendationsPage recommendations={recommendations} summary={recommendationSummary} onEdit={handleEditRecommendation} onDelete={handleDeleteRecommendation} />}
          {activeNav === "applications"    && <ApplicationsView onNavTo={navTo} onRaiseTicket={openRaiseTicket} />}
          {activeNav === "candidates"      && <CandidatesView   onNavTo={navTo} onRaiseTicket={openRaiseTicket} />}
          {activeNav === "jobs"            && <JobsView postedJobs={postedJobs} onJobPosted={handleJobPosted} employerId={employerId} companyName={companyName} onRefresh={fetchMyJobs} />}
          {activeNav === "pipeline"        && <PipelineView />}
          {activeNav === "reports"         && <ReportsView appCounts={appCounts} postedJobs={postedJobs} />}
          {activeNav === "complaints"      && <ComplaintsView onNavTo={navTo} onRaiseTicket={openRaiseTicket} />}
          {activeNav === "interviews"      && (
            <div className="view-anim">
              <div className="page-hero"><div><h1 className="page-h1">Interviews</h1><p className="page-sub">Schedule interviews from the Applications tab</p></div></div>
              <div className="empty-state">
                <span style={{ fontSize: "3rem" }}>📅</span>
                <p style={{ fontWeight: 700 }}>No interviews scheduled yet</p>
                <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={() => navTo("applications")}>Go to Applications →</button>
              </div>
            </div>
          )}
          {activeNav === "salary-slip" && <SalarySlipCalculator />}
          {activeNav === "EmpManage"   && <EmpManage />}
          {activeNav === "documents"   && <EmployeeDocumentManagement />}
          {activeNav === "payroll"     && <Payroll />}
        </div>
      </main>

      {/* Root-level ComplaintModal — rendered outside all view overlays */}
      {complaintTarget && (
        <ComplaintModal
          key={complaintTarget.id}
          candidate={complaintTarget}
          onClose={closeComplaintModal}
          onCreated={() => { closeComplaintModal(); }}
        />
      )}
    </div>
  );
}